import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Collapse,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Divider,
    Alert
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { G06BlockDataGrid } from "../Common/G06BlockDataGrid";
import { API_BASE_URL } from "../../utils/constants";
import { describeG06Status, titleForG06Status } from "../../utils/g06ErrorMessages";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const LEVEL_COUNT = 10;
const RCC_COUNT = 100;
const IGCC_MIN = 1;
const IGCC_MAX = 3;
const IGCC_DEFAULT = 3;
const UNKNOWN_DEFAULT = 12;
const UNKNOWN_MAX = 200;
const DEFAULT_VISIBLE_RCC_COLS = 24;
const MIN_RCC_ROWS = 5;
const MIN_RCC_COLS = 1;
const REF_COL_WIDTH = 70;
const CONC_COL_WIDTH = 120;
const RCC_COL_WIDTH = 62;
const MIN_RCC_COL_WIDTH = 48;
const GRID_FIT_BUFFER_PX = 4;
const GRID_CONTENT_EXTRA_WIDTH_PX = 8;
// Backend base URL for the G06 page. IGCC posts to /fitpooled because the
// /igcc alias is not always present on the deployed server; the fitPooled
// handler reads optional igccConcs/igccSignals/unknownAlus/unknownDoses
// arrays from the request body and dispatches accordingly.
const API_BASE = (API_BASE_URL || "").replace(/\/+$/, "") + "/";
const RAWCURVE_POOLED_URL = `${API_BASE}api/v1/rawcurve/fitpooled`;
const RAWCURVE_IGCC_URL = RAWCURVE_POOLED_URL;

// Frontend page + backend math-library version metadata, surfaced at the
// bottom of the page so the user can quickly identify what they are running
// when reporting issues or comparing results across analyzers.  The math
// library string mirrors the SCCS id from g06curv.c ("@(#)g06curv.c 5.1
// 03/14/97"), which is what CP_G06Math.cpp wraps unchanged in the backend.
const APP_VERSION = "1.0.0";
const MATH_LIB_VERSION = "G06 v5.1 (1997-03-14, GAPS-derived protein5 fit)";

const createEmptyRccRow = (index) => {
    const row = { id: index, level: index + 1, concentration: null };
    for (let r = 1; r <= RCC_COUNT; r++) row[`RCC-${r}`] = null;
    return row;
};

const parseMaybeNumber = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const s = String(v).trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

// Format number with significant digits and precision digits
// significantDigits: total significant figures
// precisionDigits: decimal places
// Linear interpolation of y given x on a polyline of {x,y} points sorted by x ascending.
const interpolateSignalAtDose = (dose, points) => {
    if (!Array.isArray(points) || points.length < 2) return null;
    const x = Number(dose);
    if (!Number.isFinite(x)) return null;
    if (x <= points[0].x) {
        const a = points[0], b = points[1];
        if (b.x === a.x) return a.y;
        return a.y + ((x - a.x) * (b.y - a.y)) / (b.x - a.x);
    }
    const last = points.length - 1;
    if (x >= points[last].x) {
        const a = points[last - 1], b = points[last];
        if (b.x === a.x) return b.y;
        return a.y + ((x - a.x) * (b.y - a.y)) / (b.x - a.x);
    }
    for (let i = 0; i < last; i++) {
        const a = points[i], b = points[i + 1];
        if (x >= a.x && x <= b.x) {
            if (b.x === a.x) return a.y;
            return a.y + ((x - a.x) * (b.y - a.y)) / (b.x - a.x);
        }
    }
    return null;
};

// Inverse interpolation: find dose x given signal y by walking monotonic segments.
const interpolateDoseAtSignal = (signal, points) => {
    if (!Array.isArray(points) || points.length < 2) return null;
    const y = Number(signal);
    if (!Number.isFinite(y)) return null;
    for (let i = 0; i < points.length - 1; i++) {
        const a = points[i], b = points[i + 1];
        const ymin = Math.min(a.y, b.y);
        const ymax = Math.max(a.y, b.y);
        if (y >= ymin && y <= ymax) {
            if (b.y === a.y) return a.x;
            return a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y);
        }
    }
    // Out of range: extrapolate from nearest end segment
    const overallStartY = points[0].y;
    const overallEndY = points[points.length - 1].y;
    const useStart = Math.abs(y - overallStartY) <= Math.abs(y - overallEndY);
    const a = useStart ? points[0] : points[points.length - 2];
    const b = useStart ? points[1] : points[points.length - 1];
    if (b.y === a.y) return null;
    return a.x + ((y - a.y) * (b.x - a.x)) / (b.y - a.y);
};

const formatWithRounding = (v, significantDigits, precisionDigits) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);

    // If value is 0, just return with precision
    if (n === 0) return n.toFixed(precisionDigits);

    // Round to significant digits first
    const magnitude = Math.floor(Math.log10(Math.abs(n))) + 1;
    const scale = Math.pow(10, significantDigits - magnitude);
    const rounded = Math.round(n * scale) / scale;

    // Then format with precision digits
    return rounded.toFixed(precisionDigits);
};

// Validate user-entered RCC data for two common transcription errors that
// would otherwise produce nonsense fits or backend errors:
//   (1) Concentration column not strictly monotonically increasing across
//       reference levels - e.g. row 4 is 0.5 but row 5 is 0.1.
//   (2) One RCC running opposite to the rest - e.g. all curves descending
//       but RCC-3 is randomly ascending, which usually means values were
//       pasted into the wrong row or column.
// Returns { ok: bool, message: string }.
const validateRccData = (concs, rccSignals) => {
    for (let i = 1; i < concs.length; i++) {
        if (!(concs[i] > concs[i - 1])) {
            return {
                ok: false,
                message: `Concentration at row ${i + 1} (${concs[i]}) is not greater than row ${i} (${concs[i - 1]}). Re-order the rows so that concentration strictly increases with the reference level.`
            };
        }
    }

    if (!Array.isArray(rccSignals) || rccSignals.length === 0) {
        return { ok: true, message: "" };
    }

    // (2a) Per-RCC monotonicity: every consecutive pair within one RCC must
    // step in a single direction (allowing exact ties).  Catches transcription
    // errors like 5 -> 2 -> 10, where first vs last alone would mis-classify
    // the curve as ascending.
    for (let i = 0; i < rccSignals.length; i++) {
        const sigs = rccSignals[i];
        if (!Array.isArray(sigs) || sigs.length < 2) continue;
        let sawUp = false;
        let sawDown = false;
        let breakAt = -1;
        for (let j = 1; j < sigs.length; j++) {
            if (sigs[j] > sigs[j - 1]) sawUp = true;
            else if (sigs[j] < sigs[j - 1]) sawDown = true;
            if (sawUp && sawDown) { breakAt = j; break; }
        }
        if (sawUp && sawDown) {
            return {
                ok: false,
                message: `RCC #${i + 1} signal is not monotonic: row ${breakAt + 1} (${sigs[breakAt]}) reverses direction relative to row ${breakAt} (${sigs[breakAt - 1]}). Check the column for transcription errors or swapped rows.`
            };
        }
    }

    // (2b) Cross-RCC trend: every RCC should run the same overall direction.
    let ascCount = 0, descCount = 0;
    for (const sigs of rccSignals) {
        if (!Array.isArray(sigs) || sigs.length < 2) continue;
        const first = sigs[0];
        const last = sigs[sigs.length - 1];
        if (last > first) ascCount++;
        else if (last < first) descCount++;
    }
    if (ascCount === 0 && descCount === 0) {
        return { ok: true, message: "" };
    }
    const overallAsc = ascCount >= descCount;
    const overallLabel = overallAsc ? "ascending" : "descending";

    for (let i = 0; i < rccSignals.length; i++) {
        const sigs = rccSignals[i];
        if (!Array.isArray(sigs) || sigs.length < 2) continue;
        const first = sigs[0];
        const last = sigs[sigs.length - 1];
        if (first === last) continue;
        const thisAsc = last > first;
        if (thisAsc !== overallAsc) {
            return {
                ok: false,
                message: `RCC #${i + 1} runs ${thisAsc ? "ascending" : "descending"} while the rest of the curves run ${overallLabel}. Check the column for a transcription error or a swapped row.`
            };
        }
    }

    return { ok: true, message: "" };
};

const G06 = () => {
    const [metaOpen, setMetaOpen] = useState(true);
    const [rccOpen, setRccOpen] = useState(true);
    const [igccOpen, setIgccOpen] = useState(false);
    const [unknownsOpen, setUnknownsOpen] = useState(false);
    const [roundOpen, setRoundOpen] = useState(true);
    const [resultOpen, setResultOpen] = useState(false);

    const [errorDialog, setErrorDialog] = useState({ open: false, title: "", message: "" });
    const showError = useCallback((title, message) => {
        setErrorDialog({ open: true, title, message });
    }, []);
    const closeError = useCallback(() => {
        setErrorDialog({ open: false, title: "", message: "" });
    }, []);

    const [operationStatus, setOperationStatus] = useState({
        message: "Ready",
        busy: false,
        level: "info",
        at: new Date()
    });

    const startOperation = useCallback((message) => {
        setOperationStatus({ message, busy: true, level: "info", at: new Date() });
    }, []);

    const finishOperation = useCallback((message, level = "success") => {
        setOperationStatus({ message, busy: false, level, at: new Date() });
    }, []);

    const [metadata, setMetadata] = useState({
        analyteCode: "",
        assayName: "",
        reagentLot: "",
        mgccId: "",
        protocolId: "",
        protocolNumber: "",
        owner: "",
        date: "",
        genId: "",
        analyzerId: ""
    });

    const [selectedRCCs, setSelectedRCCs] = useState(
        Array(RCC_COUNT).fill(true)
    );

    const [rccAnalyzerIds, setRccAnalyzerIds] = useState(Array(RCC_COUNT).fill(""));
    const [rccIds, setRccIds] = useState(Array(RCC_COUNT).fill(""));
    const [visibleRccCount, setVisibleRccCount] = useState(Math.min(DEFAULT_VISIBLE_RCC_COLS, RCC_COUNT));

    const toggleRCC = useCallback((index) => {
        setSelectedRCCs(prev => {
            const copy = [...prev];
            copy[index] = !copy[index];
            return copy;
        });
    }, []);

    const [gridData, setGridData] = useState(
        Array.from({ length: LEVEL_COUNT }, (_, i) => createEmptyRccRow(i))
    );
    const [rccRowCount, setRccRowCount] = useState(LEVEL_COUNT);

    const fileInputRef = useRef(null);
    const igccFileInputRef = useRef(null);
    const unkAluFileInputRef = useRef(null);
    const unkDoseFileInputRef = useRef(null);
    const gridContainerRef = useRef(null);
    // SVG element handle, used by copyToExcel to render the dose-response
    // chart to a PNG download alongside the clipboard TSV.
    const svgRef = useRef(null);
    const [gridContainerWidth, setGridContainerWidth] = useState(0);

    useEffect(() => {
        if (!gridContainerRef.current) return;

        const updateWidth = () => {
            const w = gridContainerRef.current?.clientWidth || 0;
            setGridContainerWidth(w);
        };

        updateWidth();

        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(gridContainerRef.current);

        return () => observer.disconnect();
    }, []);

    const rccColWidth = useMemo(() => {
        const fitColumns = Math.min(DEFAULT_VISIBLE_RCC_COLS, RCC_COUNT);
        const borderAllowance = fitColumns + 2;
        const available = Math.max(
            0,
            gridContainerWidth - REF_COL_WIDTH - CONC_COL_WIDTH - GRID_FIT_BUFFER_PX - borderAllowance
        );
        if (available <= 0) return MIN_RCC_COL_WIDTH;
        return Math.max(MIN_RCC_COL_WIDTH, Math.floor(available / fitColumns));
    }, [gridContainerWidth]);

    const totalGridWidth = REF_COL_WIDTH + CONC_COL_WIDTH + (visibleRccCount * rccColWidth);
    const effectiveGridWidth = totalGridWidth + GRID_CONTENT_EXTRA_WIDTH_PX;

    const handleImportMGCC = useCallback((event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        startOperation(`Importing RCC data from ${file.name}...`);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

                if (jsonRows.length < 2) {
                    showError("Import Failed", "File appears empty or has no data rows.");
                    finishOperation("Import failed: file is empty.", "error");
                    return;
                }

                // Treat as RCC raw data import
                let headerRowIdx = -1;
                let concColIdx = -1;
                let rccStartColIdx = -1;

                for (let r = 0; r < Math.min(jsonRows.length, 15); r++) {
                    const row = jsonRows[r].map(c => String(c).trim().toLowerCase());
                    for (let c = 0; c < row.length; c++) {
                        if (row[c] === "conc" || row[c] === "concentration" || row[c] === "conc.") {
                            headerRowIdx = r;
                            concColIdx = c;
                            rccStartColIdx = c + 1;
                            break;
                        }
                    }
                    if (headerRowIdx >= 0) break;
                }

                if (headerRowIdx < 0) {
                    headerRowIdx = 0;
                    concColIdx = 0;
                    rccStartColIdx = 1;
                }

                // Optional: parse Analyzer ID / ID rows if present above the header row
                if (headerRowIdx >= 2) {
                    const analyzerRow = jsonRows[headerRowIdx - 2] || [];
                    const idRow = jsonRows[headerRowIdx - 1] || [];

                    const maybeAnalyzerLabel = String(analyzerRow[concColIdx] || "").toLowerCase();
                    const maybeIdLabel = String(idRow[concColIdx] || "").toLowerCase();

                    if (maybeAnalyzerLabel.includes("analyzer")) {
                        const importedAnalyzerIds = Array.from({ length: RCC_COUNT }, (_, idx) =>
                            String(analyzerRow[rccStartColIdx + idx] ?? "").trim()
                        );
                        setRccAnalyzerIds(importedAnalyzerIds);
                    }

                    if (maybeIdLabel === "id" || maybeIdLabel.includes(" id")) {
                        const importedIds = Array.from({ length: RCC_COUNT }, (_, idx) =>
                            String(idRow[rccStartColIdx + idx] ?? "").trim()
                        );
                        setRccIds(importedIds);
                    }
                }

                const dataRows = jsonRows.slice(headerRowIdx + 1).filter(row =>
                    row.some(cell => cell !== "" && cell !== null && cell !== undefined)
                );

                if (dataRows.length === 0) {
                    showError("Import Failed", "No data rows found after header.");
                    finishOperation("Import failed: no data rows found.", "error");
                    return;
                }

                if (dataRows.length < MIN_RCC_ROWS) {
                    showError("Import Warning", `Only ${dataRows.length} data rows found. At least ${MIN_RCC_ROWS} are recommended for curve fitting.`);
                    finishOperation(`Imported with warning: only ${dataRows.length} rows found.`, "warning");
                }

                const maxCol = Math.max(...jsonRows.slice(headerRowIdx, headerRowIdx + 2).map(r => r.length));
                const numRccs = Math.min(maxCol - rccStartColIdx, RCC_COUNT);

                if (numRccs < 1) {
                    showError("Import Failed", "No RCC signal columns found. Ensure your file has concentration in one column and signal data in subsequent columns.");
                    finishOperation("Import failed: no RCC columns found.", "error");
                    return;
                }

                const newRowCount = Math.max(dataRows.length, MIN_RCC_ROWS);
                const newGridData = Array.from({ length: newRowCount }, (_, i) => {
                    const row = createEmptyRccRow(i);
                    if (i < dataRows.length) {
                        const src = dataRows[i];
                        const concVal = parseMaybeNumber(src[concColIdx]);
                        if (concVal !== null) row.concentration = concVal;

                        for (let r = 0; r < numRccs; r++) {
                            const sigVal = parseMaybeNumber(src[rccStartColIdx + r]);
                            if (sigVal !== null) row[`RCC-${r + 1}`] = sigVal;
                        }
                    }
                    return row;
                });

                setGridData(newGridData);
                setRccRowCount(newRowCount);
                setVisibleRccCount(Math.max(MIN_RCC_COLS, numRccs));
                setRccOpen(true);
                finishOperation(`RCC import complete: ${dataRows.length} rows, ${numRccs} RCC curves loaded.`, "success");

            } catch (err) {
                console.error("Import error:", err);
                showError("Import Error", `Failed to read file: ${err.message}`);
                finishOperation("Import failed due to file parsing error.", "error");
            }
        };
        reader.readAsArrayBuffer(file);

        // Reset input so same file can be re-imported
        event.target.value = "";
    }, [showError, startOperation, finishOperation]);

    // -------------------------------------------------------------------------
    // Generic helpers for IGCC / Unknown Excel imports.
    // Each importer accepts files exported by Calculate IGCC's "Export to Excel"
    // option, and also stand-alone sheets where the column headers describe the
    // data. Recognised header tokens (case-insensitive):
    //   ID:          id, label, sample, sample id, cal, cal id, calibrator
    //   ALU input:   alu, signal, input alu, measured alu, response
    //   Dose input:  dose, conc, concentration, input dose, known dose
    // The header row may be the very first row, or the importer will fall back
    // to assuming columns are [ID, value1, value2].
    // -------------------------------------------------------------------------
    const HDR_ID    = ["id", "label", "sample", "sample id", "cal", "cal id", "calibrator", "name"];
    const HDR_ALU   = ["alu", "signal", "input alu", "measured alu", "response", "y", "input"];
    const HDR_DOSE  = ["dose", "conc", "concentration", "input dose", "known dose", "x"];

    const findSheetWithHeaders = (workbook, requiredAny) => {
        // Find the first sheet whose first 5 rows contain at least one of the required tokens.
        for (const name of workbook.SheetNames) {
            const sheet = workbook.Sheets[name];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
            for (let r = 0; r < Math.min(rows.length, 5); r++) {
                const tokens = rows[r].map(c => String(c).trim().toLowerCase());
                if (tokens.some(t => requiredAny.includes(t))) {
                    return { sheet, rows, headerRowIdx: r };
                }
            }
        }
        // No header match: return first sheet at row 0.
        const first = workbook.Sheets[workbook.SheetNames[0]];
        return { sheet: first, rows: XLSX.utils.sheet_to_json(first, { header: 1, defval: "" }), headerRowIdx: -1 };
    };

    const indexFor = (headers, tokens) => {
        for (let c = 0; c < headers.length; c++) {
            if (tokens.includes(headers[c])) return c;
        }
        return -1;
    };

    const readWorkbook = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                resolve(XLSX.read(data, { type: "array" }));
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = () => reject(new Error("Failed to read file."));
        reader.readAsArrayBuffer(file);
    });

    const handleImportIgcc = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        startOperation(`Importing IGCC re-calibrators from ${file.name}...`);
        try {
            const workbook = await readWorkbook(file);
            const { rows, headerRowIdx } = findSheetWithHeaders(workbook, [...HDR_ALU, ...HDR_DOSE]);

            let idCol = -1, aluCol = -1, doseCol = -1, dataStart = 0;
            if (headerRowIdx >= 0) {
                const headers = rows[headerRowIdx].map(c => String(c).trim().toLowerCase());
                idCol = indexFor(headers, HDR_ID);
                aluCol = indexFor(headers, HDR_ALU);
                doseCol = indexFor(headers, HDR_DOSE);
                dataStart = headerRowIdx + 1;
            }
            // Fallback layout: [ID, ALU, Dose]
            if (aluCol < 0 || doseCol < 0) {
                idCol = 0; aluCol = 1; doseCol = 2; dataStart = headerRowIdx >= 0 ? headerRowIdx + 1 : 0;
            }

            const collected = [];
            for (let r = dataStart; r < rows.length; r++) {
                const row = rows[r];
                if (!row || row.every(c => c === "" || c === null || c === undefined)) continue;
                const alu = parseMaybeNumber(row[aluCol]);
                const dose = parseMaybeNumber(row[doseCol]);
                if (alu === null && dose === null) continue;
                collected.push({
                    calId: String(row[idCol] ?? `CAL${collected.length + 1}`).trim() || `CAL${collected.length + 1}`,
                    inputAlu: alu === null ? "" : String(alu),
                    inputDose: dose === null ? "" : String(dose)
                });
                if (collected.length >= IGCC_MAX) break;
            }

            if (collected.length === 0) {
                showError("Import Failed", "No IGCC rows found. Expected columns ID, ALU, Dose.");
                finishOperation("IGCC import failed: no rows.", "error");
                return;
            }

            setIgccRows(collected.map((c, i) => ({ id: i, ...c })));
            setIgccOpen(true);
            finishOperation(`IGCC import complete: ${collected.length} re-calibrator${collected.length === 1 ? "" : "s"} loaded.`, "success");
        } catch (err) {
            console.error("IGCC import error:", err);
            showError("Import Error", `Failed to read file: ${err.message}`);
            finishOperation("IGCC import failed.", "error");
        }
    };

    const importUnknownGeneric = async (file, mode /* "alu" | "dose" */) => {
        const workbook = await readWorkbook(file);
        const wantTokens = mode === "alu" ? HDR_ALU : HDR_DOSE;
        const { rows, headerRowIdx } = findSheetWithHeaders(workbook, wantTokens);

        let idCol = -1, valCol = -1, dataStart = 0;
        if (headerRowIdx >= 0) {
            const headers = rows[headerRowIdx].map(c => String(c).trim().toLowerCase());
            idCol = indexFor(headers, HDR_ID);
            valCol = indexFor(headers, wantTokens);
            dataStart = headerRowIdx + 1;
        }
        if (valCol < 0) { idCol = 0; valCol = 1; dataStart = headerRowIdx >= 0 ? headerRowIdx + 1 : 0; }

        const collected = [];
        for (let r = dataStart; r < rows.length; r++) {
            const row = rows[r];
            if (!row || row.every(c => c === "" || c === null || c === undefined)) continue;
            const v = parseMaybeNumber(row[valCol]);
            if (v === null) continue;
            collected.push({
                sampleId: String(row[idCol] ?? `S${collected.length + 1}`).trim() || `S${collected.length + 1}`,
                value: String(v)
            });
            if (collected.length >= UNKNOWN_MAX) break;
        }
        return collected;
    };

    const handleImportUnknownAlu = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        startOperation(`Importing unknown ALU samples from ${file.name}...`);
        try {
            const collected = await importUnknownGeneric(file, "alu");
            if (collected.length === 0) {
                showError("Import Failed", "No unknown ALU rows found. Expected columns ID, ALU.");
                finishOperation("Unknown ALU import failed: no rows.", "error");
                return;
            }
            setUnknownAluRows(collected.map((c, i) => ({
                id: i,
                sampleId: c.sampleId,
                inputAlu: c.value,
                fittedDose: ""
            })));
            setUnknownsOpen(true);
            finishOperation(`Unknown ALU import complete: ${collected.length} sample${collected.length === 1 ? "" : "s"} loaded.`, "success");
        } catch (err) {
            console.error("Unknown ALU import error:", err);
            showError("Import Error", `Failed to read file: ${err.message}`);
            finishOperation("Unknown ALU import failed.", "error");
        }
    };

    const handleImportUnknownDose = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        startOperation(`Importing unknown Dose samples from ${file.name}...`);
        try {
            const collected = await importUnknownGeneric(file, "dose");
            if (collected.length === 0) {
                showError("Import Failed", "No unknown Dose rows found. Expected columns ID, Dose.");
                finishOperation("Unknown Dose import failed: no rows.", "error");
                return;
            }
            setUnknownDoseRows(collected.map((c, i) => ({
                id: i,
                sampleId: c.sampleId,
                inputDose: c.value,
                fittedAlu: ""
            })));
            setUnknownsOpen(true);
            finishOperation(`Unknown Dose import complete: ${collected.length} sample${collected.length === 1 ? "" : "s"} loaded.`, "success");
        } catch (err) {
            console.error("Unknown Dose import error:", err);
            showError("Import Error", `Failed to read file: ${err.message}`);
            finishOperation("Unknown Dose import failed.", "error");
        }
    };

    // Copy both unknowns tables (Input ALU -> Fitted Dose, Input Dose ->
    // Fitted ALU) to the clipboard as TSV, ready to paste straight into
    // Excel.  The full .xlsx export with the embedded chart is still
    // available from the MGCC Information panel below; this button is the
    // lightweight "just the unknown samples" path for users who only need
    // those two tables in their report.
    const handleCopyUnknowns = async () => {
        try {
            const exportedAt = new Date();
            const stampDisplay = exportedAt.toLocaleString();

            const lines = [];
            lines.push(["Exported:", stampDisplay, "", "Operation:", lastOperation].join("\t"));
            lines.push("");
            lines.push("Unknowns: Input ALU -> Fitted Dose");
            lines.push(["ID", "Input ALU", "Fitted Dose"].join("\t"));
            for (const r of unknownAluRows) {
                lines.push([r.sampleId ?? "", r.inputAlu ?? "", r.fittedDose ?? ""].join("\t"));
            }
            lines.push("");
            lines.push("Unknowns: Input Dose -> Fitted ALU");
            lines.push(["ID", "Input Dose", "Fitted ALU"].join("\t"));
            for (const r of unknownDoseRows) {
                lines.push([r.sampleId ?? "", r.inputDose ?? "", r.fittedAlu ?? ""].join("\t"));
            }

            const tsv = lines.join("\n");
            await navigator.clipboard.writeText(tsv);
            finishOperation(
                `Unknowns copied to clipboard (${unknownAluRows.length} ALU -> Dose, ${unknownDoseRows.length} Dose -> ALU). Paste into Excel.`,
                "success"
            );
        } catch (err) {
            console.error("Copy unknowns error:", err);
            showError("Copy Failed", err?.message || "Failed to copy unknowns to clipboard.");
            finishOperation("Copy unknowns failed.", "error");
        }
    };

    const handleAddColumns = useCallback((count = 1) => {
        const safeCount = Math.max(1, count);
        setVisibleRccCount((prev) => {
            const next = Math.min(RCC_COUNT, prev + safeCount);
            const added = next - prev;
            if (added > 0) {
                finishOperation(`Added ${added} RCC column${added > 1 ? "s" : ""}.`, "info");
            } else {
                finishOperation(`Cannot add columns beyond maximum of ${RCC_COUNT}.`, "warning");
            }
            return next;
        });
    }, [finishOperation]);

    const handleRemoveColumns = useCallback((count = 1) => {
        const safeCount = Math.max(1, count);
        setVisibleRccCount((prev) => {
            const next = Math.max(MIN_RCC_COLS, prev - safeCount);
            const removed = prev - next;
            if (removed > 0) {
                finishOperation(`Removed ${removed} RCC column${removed > 1 ? "s" : ""}.`, "info");
            } else {
                finishOperation(`Cannot remove columns below minimum of ${MIN_RCC_COLS}.`, "warning");
            }
            return next;
        });
    }, [finishOperation]);

    // Clear the RCC grid back to its initial empty state. Wipes
    // concentration + every RCC-N cell, analyzer / calibrator ID rows, and
    // resets row / column counts to the page defaults so the grid looks
    // exactly like a fresh page load. IGCC / unknowns / fit results are
    // left alone - the user can re-import RCC data without losing the work
    // they may have queued up on the per-RCC tabs.
    const handleClearRccData = useCallback(() => {
        setGridData(Array.from({ length: LEVEL_COUNT }, (_, i) => createEmptyRccRow(i)));
        setRccRowCount(LEVEL_COUNT);
        setRccAnalyzerIds(Array(RCC_COUNT).fill(""));
        setRccIds(Array(RCC_COUNT).fill(""));
        setVisibleRccCount(Math.min(DEFAULT_VISIBLE_RCC_COLS, RCC_COUNT));
        setSelectedRCCs(Array(RCC_COUNT).fill(true));
        finishOperation("RCC table cleared.", "info");
    }, [finishOperation]);

    const [rounding, setRounding] = useState({
        significantDigits: 4,
        precisionDigits: 3,
        cvSignificantDigits: 3,
        cvPrecisionDigits: 1
    });

    const [mgccRows, setMgccRows] = useState(
        Array.from({ length: 10 }, (_, i) => ({
            id: i,
            ref: i + 1,
            conc: null,
            pooled: null,
            cv: null,
            fittedConc: null,
            fittedSignal: null
        }))
    );

    const [fitFactor, setFitFactor] = useState("");
    const [shapeFactor, setShapeFactor] = useState("");
    const [abcdn, setAbcdn] = useState({ a: "", b: "", c: "", d: "", n: "" });

    const [graphLinePoints, setGraphLinePoints] = useState([]);
    const [graphRawPoints, setGraphRawPoints] = useState([]);
    const [hoveredPoint, setHoveredPoint] = useState(null);

    // Human-readable label of the last successful fit, surfaced in the
    // Copy-to-Excel header so the user can tell at a glance whether the
    // exported numbers came from a plain RCC fit or an IGCC re-calibration.
    const [lastOperation, setLastOperation] = useState("(none)");

    // User-customizable title rendered at the top of the dose-response
    // chart (on-page and in every exported PNG). Defaults to the original
    // hardcoded label so existing screenshots and reports stay consistent
    // for users who never touch it.
    const [chartTitle, setChartTitle] = useState("Dose Curve Response");

    // Tracks which fit is currently displayed in the chart / MGCC info panel.
    // Either "RCC-N" for a single-RCC fit, "Master" for the all-columns fit,
    // or "" when no fit has been run yet. Surfaced as a banner on the chart
    // so the user sees they may be looking at stale per-RCC data after
    // switching tabs.
    const [activeFitLabel, setActiveFitLabel] = useState("");

    // Records which pool the most recent Generate established so a later
    // Calculate IGCC can re-use the same scope.
    //   null              -> no Generate has been run yet (IGCC must error)
    //   { mode: "master",    excluded: [3, 7] }  -> all enabled RCCs were pooled
    //   { mode: "single", rcc: 16 }              -> only RCC-16 was used
    // The excluded list is captured at Generate-time so subsequent IGCC
    // clicks reproduce the same pool even if the user later re-ticks
    // checkboxes.
    const [lastMasterPool, setLastMasterPool] = useState(null);

    // Per-RCC flag set when Generate MGCC (RCC-N) has been run for that
    // specific N. Find Unknowns is gated by this map: you cannot find
    // unknowns for RCC-N without first establishing RCC-N's 1-column
    // curve. Generate Master Curve does NOT flip these flags - unknowns
    // are scoped per-RCC and need a per-RCC fit.
    const [perRccFitDone, setPerRccFitDone] = useState({});

    // ---------------- Per-RCC IGCC / Unknown state -----------------------
    // IGCC and Unknown inputs are scoped PER RCC column - not the whole
    // master curve. The user works on one RCC at a time: Generate MGCC and
    // Calculate IGCC act on the selected RCC's column alone, evaluating
    // unknowns against that 1-RCC fit. Generate Master Curve is the only
    // operation that uses every enabled RCC simultaneously, and it does
    // NOT touch the per-RCC unknown tables.
    //
    // State shape: each map is keyed by RCC number (1..RCC_COUNT). Every
    // slot is pre-allocated (eager init) so switching tabs never has to
    // hydrate state mid-render.
    const [selectedRccTab, setSelectedRccTab] = useState(1);

    const makeDefaultIgccRows = () =>
        Array.from({ length: IGCC_DEFAULT }, (_, i) => ({
            id: i,
            calId: `CAL${i + 1}`,
            inputAlu: "",
            inputDose: ""
        }));
    const makeDefaultUnknownAluRows = () =>
        Array.from({ length: UNKNOWN_DEFAULT }, (_, i) => ({
            id: i,
            sampleId: "",
            inputAlu: "",
            fittedDose: ""
        }));
    const makeDefaultUnknownDoseRows = () =>
        Array.from({ length: UNKNOWN_DEFAULT }, (_, i) => ({
            id: i,
            sampleId: "",
            inputDose: "",
            fittedAlu: ""
        }));

    const makeInitialPerRccMap = (factory) => {
        const m = {};
        for (let i = 1; i <= RCC_COUNT; i++) m[i] = factory();
        return m;
    };

    const [igccByRcc, setIgccByRcc] = useState(() => makeInitialPerRccMap(makeDefaultIgccRows));
    const [unknownAluByRcc, setUnknownAluByRcc] = useState(() => makeInitialPerRccMap(makeDefaultUnknownAluRows));
    const [unknownDoseByRcc, setUnknownDoseByRcc] = useState(() => makeInitialPerRccMap(makeDefaultUnknownDoseRows));

    // Derived views: the currently-selected tab's slice. Everything
    // downstream (UI, validators, payload builders) reads these so the
    // existing code paths keep working unchanged.
    const igccRows = igccByRcc[selectedRccTab] || makeDefaultIgccRows();
    const unknownAluRows = unknownAluByRcc[selectedRccTab] || makeDefaultUnknownAluRows();
    const unknownDoseRows = unknownDoseByRcc[selectedRccTab] || makeDefaultUnknownDoseRows();

    // Writers route through the selected RCC tab.
    const setIgccRows = useCallback((updater) => {
        setIgccByRcc(prev => {
            const current = prev[selectedRccTab] || makeDefaultIgccRows();
            const next = typeof updater === "function" ? updater(current) : updater;
            return { ...prev, [selectedRccTab]: next };
        });
    }, [selectedRccTab]);
    const setUnknownAluRows = useCallback((updater) => {
        setUnknownAluByRcc(prev => {
            const current = prev[selectedRccTab] || makeDefaultUnknownAluRows();
            const next = typeof updater === "function" ? updater(current) : updater;
            return { ...prev, [selectedRccTab]: next };
        });
    }, [selectedRccTab]);
    const setUnknownDoseRows = useCallback((updater) => {
        setUnknownDoseByRcc(prev => {
            const current = prev[selectedRccTab] || makeDefaultUnknownDoseRows();
            const next = typeof updater === "function" ? updater(current) : updater;
            return { ...prev, [selectedRccTab]: next };
        });
    }, [selectedRccTab]);

    const addIgccRow = useCallback(() => {
        setIgccRows(prev => {
            if (prev.length >= IGCC_MAX) return prev;
            return [...prev, {
                id: prev.length,
                calId: `CAL${prev.length + 1}`,
                inputAlu: "",
                inputDose: ""
            }];
        });
    }, [setIgccRows]);

    const removeIgccRow = useCallback(() => {
        setIgccRows(prev => (prev.length <= IGCC_MIN ? prev : prev.slice(0, -1)));
    }, [setIgccRows]);

    const addUnknownAluRow = useCallback(() => {
        setUnknownAluRows(prev => {
            if (prev.length >= UNKNOWN_MAX) return prev;
            return [...prev, { id: prev.length, sampleId: "", inputAlu: "", fittedDose: "" }];
        });
    }, [setUnknownAluRows]);
    const removeUnknownAluRow = useCallback(() => {
        setUnknownAluRows(prev => (prev.length <= 1 ? prev : prev.slice(0, -1)));
    }, [setUnknownAluRows]);

    const addUnknownDoseRow = useCallback(() => {
        setUnknownDoseRows(prev => {
            if (prev.length >= UNKNOWN_MAX) return prev;
            return [...prev, { id: prev.length, sampleId: "", inputDose: "", fittedAlu: "" }];
        });
    }, [setUnknownDoseRows]);
    const removeUnknownDoseRow = useCallback(() => {
        setUnknownDoseRows(prev => (prev.length <= 1 ? prev : prev.slice(0, -1)));
    }, [setUnknownDoseRows]);

    // When RCC columns are removed, fall back to the highest remaining tab
    // if the previous selection is no longer addressable.
    useEffect(() => {
        if (selectedRccTab > visibleRccCount) {
            setSelectedRccTab(Math.max(1, visibleRccCount));
        }
    }, [visibleRccCount, selectedRccTab]);

    // Live preview of fitted unknown values from the cached curve points.
    // The authoritative values come from the backend "Calculate IGCC" button.
    useEffect(() => {
        if (graphLinePoints.length < 2) return;
        const sigDigits = rounding.significantDigits;
        const precDigits = rounding.precisionDigits;

        setUnknownAluRows(prev => prev.map(r => {
            const alu = parseMaybeNumber(r.inputAlu);
            if (alu === null) return r.fittedDose === "" ? r : { ...r, fittedDose: "" };
            const dose = interpolateDoseAtSignal(alu, graphLinePoints);
            const formatted = dose === null ? "" : formatWithRounding(dose, sigDigits, precDigits);
            return r.fittedDose === formatted ? r : { ...r, fittedDose: formatted };
        }));

        setUnknownDoseRows(prev => prev.map(r => {
            const dose = parseMaybeNumber(r.inputDose);
            if (dose === null) return r.fittedAlu === "" ? r : { ...r, fittedAlu: "" };
            const alu = interpolateSignalAtDose(dose, graphLinePoints);
            const formatted = alu === null ? "" : formatWithRounding(alu, sigDigits, precDigits);
            return r.fittedAlu === formatted ? r : { ...r, fittedAlu: formatted };
        }));
    }, [graphLinePoints, rounding]);

    // Plot points for IGCC re-calibrators on the chart (Dose, ALU).
    // Only rendered after a successful Calculate IGCC has bent the curve -
    // before that the IGCC inputs are still being typed and shouldn't pollute
    // the chart with stray markers. activeFitLabel carries "IGCC bent" only
    // when the backend confirmed igcc_applied === true in handleCalculateIgcc.
    const igccBendActive = typeof activeFitLabel === "string" && activeFitLabel.includes("IGCC bent");
    const graphIgccPoints = useMemo(() => {
        if (!igccBendActive) return [];
        return igccRows
            .map(r => {
                const x = parseMaybeNumber(r.inputDose);
                const y = parseMaybeNumber(r.inputAlu);
                if (x === null || y === null) return null;
                return { x, y, label: r.calId };
            })
            .filter(Boolean);
    }, [igccRows, igccBendActive]);

    // Build column definitions with checkboxes in RCC headers
    const columnDefs = useMemo(() => {
        const defs = [
            {
                key: "Ref",
                name: "Ref",
                width: REF_COL_WIDTH,
                editable: false
            },
            {
                key: "Concentration",
                name: "Concentration",
                width: CONC_COL_WIDTH
            }
        ];
        
        for (let i = 0; i < visibleRccCount; i++) {
            const rccIndex = i;
            defs.push({
                key: `RCC-${i + 1}`,
                name: `RCC-${i + 1}`,
                width: rccColWidth,
                headerRenderer: ({ column }) => (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        gap: 0.25
                    }}>
                        <span style={{ fontSize: '10px' }}>{column.name}</span>
                        <Checkbox
                            size="small"
                            checked={selectedRCCs[rccIndex]}
                            onChange={(e) => {
                                e.stopPropagation();
                                toggleRCC(rccIndex);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 12 } }}
                        />
                    </Box>
                )
            });
        }
        
        return defs;
    }, [selectedRCCs, toggleRCC, rccColWidth, visibleRccCount]);

    // Build data for backend pooled fit
    // Returns concentrations and RCC signals in format needed for G06 pooling.
    // Any row that has a concentration but a blank signal in one or more
    // active RCC columns (or a blank concentration with at least one signal
    // filled) is reported via the `missing` array so callers can surface a
    // precise error instead of silently dropping the row.
    //
    // Options:
    //   rccFilter - optional RCC number (1-based). When provided, restricts
    //               the enabled set to that one column only, ignoring the
    //               header-checkbox state. Used by per-RCC Generate MGCC /
    //               Calculate IGCC / Find Unknowns so those operations
    //               always work on the user's selected tab regardless of
    //               whether that column is included in the master-curve
    //               pool.
    //   rccSet    - optional explicit array of RCC numbers to pool. Used by
    //               Calculate IGCC / Find Unknowns when replaying the exact
    //               pool the last Generate Master Curve established
    //               (snapshot of enabled RCCs at that moment).
    const buildRccData = ({ rccFilter = null, rccSet = null } = {}) => {
        const enabledRccs = [];
        if (rccFilter !== null) {
            if (rccFilter >= 1 && rccFilter <= visibleRccCount) enabledRccs.push(rccFilter);
        } else if (Array.isArray(rccSet)) {
            for (const n of rccSet) {
                if (n >= 1 && n <= visibleRccCount) enabledRccs.push(n);
            }
        } else {
            for (let i = 0; i < visibleRccCount; i++) {
                if (selectedRCCs[i]) enabledRccs.push(i + 1);
            }
        }

        // First pass: find which RCCs actually have ANY data
        const rccsWithData = [];
        for (const rcc of enabledRccs) {
            let hasData = false;
            for (let rowIdx = 0; rowIdx < rccRowCount; rowIdx++) {
                const row = gridData[rowIdx];
                if (!row) continue;
                const y = parseMaybeNumber(row[`RCC-${rcc}`]);
                if (y !== null) {
                    hasData = true;
                    break;
                }
            }
            if (hasData) rccsWithData.push(rcc);
        }

        if (rccsWithData.length === 0) {
            return { concs: [], rccSignals: [], numRCCs: 0, missing: [] };
        }

        const concs = [];
        const rccSignals = []; // [rccIndex][levelIndex]
        const missing = [];    // [{ row, kind, rccs }] for callers to format

        // Initialize array for each RCC that has data
        for (let i = 0; i < rccsWithData.length; i++) {
            rccSignals.push([]);
        }

        // Walk every row that has any content. Rows with concentration but a
        // missing signal (or vice versa) are recorded in `missing` so callers
        // can surface a precise error instead of dropping the row.
        for (let rowIdx = 0; rowIdx < rccRowCount; rowIdx++) {
            const row = gridData[rowIdx];
            if (!row) continue;

            const x = parseMaybeNumber(row.concentration);
            const levelSignals = [];
            const missingRccs = [];
            let anySignalFilled = false;

            for (const rcc of rccsWithData) {
                const y = parseMaybeNumber(row[`RCC-${rcc}`]);
                if (y === null) {
                    missingRccs.push(rcc);
                } else {
                    anySignalFilled = true;
                }
                levelSignals.push(y);
            }

            const rowIsEmpty = x === null && !anySignalFilled;
            if (rowIsEmpty) continue;

            if (x === null) {
                missing.push({ row: rowIdx + 1, kind: "concentration" });
                continue;
            }

            if (missingRccs.length > 0) {
                missing.push({ row: rowIdx + 1, kind: "signal", rccs: missingRccs });
                continue;
            }

            concs.push(x);
            for (let i = 0; i < rccsWithData.length; i++) {
                rccSignals[i].push(levelSignals[i]);
            }
        }

        return { concs, rccSignals, numRCCs: rccsWithData.length, missing };
    };

    // Format a missing-cell list into a single user-facing message.  Caps the
    // detail at the first 5 rows so the dialog stays readable when an entire
    // pasted block is misaligned.
    const formatMissingCells = (missing) => {
        if (!Array.isArray(missing) || missing.length === 0) return "";
        const head = missing.slice(0, 5).map(m => {
            if (m.kind === "concentration") {
                return `Row ${m.row}: concentration cell is empty but signals were entered.`;
            }
            const list = m.rccs.length > 4
                ? `${m.rccs.slice(0, 4).join(", ")} and ${m.rccs.length - 4} more`
                : m.rccs.join(", ");
            return `Row ${m.row}: RCC-${list} ${m.rccs.length === 1 ? "is" : "are"} empty.`;
        });
        const tail = missing.length > 5 ? `\n...and ${missing.length - 5} more row${missing.length - 5 === 1 ? "" : "s"} with missing cells.` : "";
        return head.join("\n") + tail;
    };

    const resetResults = (fitMessage = "") => {
        setFitFactor(fitMessage);
        setShapeFactor("");
        setAbcdn({ a: "", b: "", c: "", d: "", n: "" });
        setMgccRows(
            Array.from({ length: 10 }, (_, i) => ({
                id: i,
                ref: i + 1,
                conc: null,
                pooled: null,
                cv: null,
                fittedConc: null,
                fittedSignal: null
            }))
        );
        setGraphLinePoints([]);
        setGraphRawPoints([]);
    };

    // Single entry point for the pooled fit. Two modes:
    //   master: fits the protein-5 curve across every enabled RCC column
    //           (header checkbox honored). Does NOT touch per-RCC unknown
    //           tables - the master fit is informational only.
    //   single-RCC: fits the curve using just one RCC column and evaluates
    //               that RCC's unknown ALU/Dose rows against the resulting
    //               1-RCC curve. This matches how IGCC re-calibrators and
    //               unknowns work in the production analyzer.
    const handleGenerate = async (mode = "master") => {
        // Leave resultOpen alone so the panel doesn't flash closed during the
        // round-trip - the success path will set it true; on error paths it
        // either stays open (familiar context) or is left to the user.
        setHoveredPoint(null);

        const isMaster = mode === "master";
        const rccFilter = isMaster ? null : selectedRccTab;
        const fitLabel = isMaster ? "Master Curve" : `RCC-${selectedRccTab}`;
        startOperation(isMaster
            ? "Generating master curve across all enabled RCC columns..."
            : `Generating fit for RCC-${selectedRccTab}...`);

        const { concs, rccSignals, numRCCs, missing } = buildRccData({ rccFilter });

        console.log(`Pooled fit (${fitLabel}) payload:`, { concs, rccSignals, numRCCs });

        if (missing.length > 0) {
            const detail = formatMissingCells(missing);
            showError(
                "RCC Data Incomplete",
                `${missing.length} row${missing.length === 1 ? " has" : "s have"} missing cell${missing.length === 1 ? "" : "s"}. Fill every cell in each populated row before generating, or clear the row entirely.\n\n${detail}`
            );
            finishOperation(`Generate (${fitLabel}) failed: ${missing.length} row${missing.length === 1 ? "" : "s"} with missing cells.`, "error");
            return;
        }

        if (concs.length === 0) {
            showError("No Data", isMaster
                ? "No valid concentration or signal data found. Please enter or import data before generating."
                : `RCC-${selectedRccTab} has no concentration/signal data. Switch to a populated RCC tab or enter values first.`);
            finishOperation(`Generate (${fitLabel}) failed: no valid concentration/signal data.`, "error");
            return;
        }

        if (numRCCs < 1) {
            showError("No RCC Curves", isMaster
                ? "No enabled RCC curves contain data. Make sure at least one RCC column has signal values and its checkbox is enabled."
                : `RCC-${selectedRccTab} contains no data.`);
            finishOperation(`Generate (${fitLabel}) failed: no active RCC curves with data.`, "error");
            return;
        }

        if (concs.length < 5) {
            showError("Insufficient Data", `Only ${concs.length} valid concentration levels found in ${fitLabel}. At least 5 levels with complete data are required for curve fitting.`);
            finishOperation(`Generate (${fitLabel}) failed: only ${concs.length} valid levels (minimum 5).`, "error");
            return;
        }

        const trendCheck = validateRccData(concs, rccSignals);
        if (!trendCheck.ok) {
            showError("RCC Data Inconsistent", trendCheck.message);
            finishOperation(`Generate (${fitLabel}) failed: ${trendCheck.message}`, "error");
            return;
        }

        // In single-RCC mode the unknowns belong to that tab and are sent in
        // so the backend evaluates them on the just-fitted 1-RCC curve. In
        // master mode unknowns are NOT sent - the master fit is informational
        // only and leaves per-RCC unknown tables frozen on their last
        // per-RCC fit result.
        const unknownAlus = isMaster
            ? []
            : unknownAluRows.map(r => parseMaybeNumber(r.inputAlu));
        const unknownDoses = isMaster
            ? []
            : unknownDoseRows.map(r => parseMaybeNumber(r.inputDose));

        const payload = {
            assay: 1,
            concs,
            rccSignals,
            unknownAlus: unknownAlus.map(v => (v === null ? NaN : v)),
            unknownDoses: unknownDoses.map(v => (v === null ? NaN : v))
        };

        try {
            const response = await axios.post(
                RAWCURVE_POOLED_URL,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            console.log("Server response:", response.data);
            const data = response.data;

            setResultOpen(true);

            if (data?.status !== 1) {
                const msg = describeG06Status(data?.status, data?.message);
                showError(titleForG06Status(data?.status), msg);
                resetResults("");
                finishOperation(`Generate failed: backend status ${data?.status ?? "unknown"} - ${msg}`, "error");
                return;
            }

            // Get rounding settings
            const sigDigits = rounding.significantDigits;
            const precDigits = rounding.precisionDigits;
            const cvSigDigits = rounding.cvSignificantDigits;
            const cvPrecDigits = rounding.cvPrecisionDigits;

            // Apply rounding to Fit Factor and Shape Factor
            setFitFactor(
                data?.fit_factor !== undefined && data?.fit_factor !== null
                    ? formatWithRounding(data.fit_factor, sigDigits, precDigits)
                    : String(data?.status ?? "")
            );

            setShapeFactor(
                data?.shape_factor !== undefined && data?.shape_factor !== null
                    ? formatWithRounding(data.shape_factor, sigDigits, precDigits)
                    : formatWithRounding(0, sigDigits, precDigits)
            );

            // Use real fitted parameters from backend with rounding
            const params = data?.params;
            if (Array.isArray(params) && params.length >= 5) {
                setAbcdn({
                    a: formatWithRounding(params[0], sigDigits, precDigits),
                    b: formatWithRounding(params[1], sigDigits, precDigits),
                    c: formatWithRounding(params[2], sigDigits, precDigits),
                    d: formatWithRounding(params[3], sigDigits, precDigits),
                    n: formatWithRounding(params[4], sigDigits, precDigits)
                });
            } else {
                setAbcdn({ a: "", b: "", c: "", d: "", n: "" });
            }

            // Get pooled signals and CV from backend (proper G06 pooling)
            const pooledSignals = data?.pooled_signals || {};
            const pooledCV = data?.pooled_cv || {};
            const derivedConcs = data?.derived_concs || {};

            const curvePts = Array.isArray(data?.curve_points)
                ? data.curve_points
                    .map((p) => ({
                        x: Number(p.x),
                        y: Number(p.y)
                    }))
                    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
                : [];

            console.log("Curve points from backend:", curvePts);
            console.log("Pooled signals from backend:", pooledSignals);
            console.log("Pooled CV from backend:", pooledCV);

            setGraphLinePoints(curvePts);
            
            // Use pooled signals for raw points on graph
            setGraphRawPoints(concs.map((c, i) => ({ 
                x: c, 
                y: pooledSignals[i] || 0 
            })));

            // Get exact fitted signals from backend (computed via ysignal at each concentration)
            const fittedSignals = data?.fitted_signals || {};

            // Build MGCC table with properly pooled data and rounding
            const mgccData = concs.map((c, i) => {
                return {
                    id: i,
                    ref: i + 1,
                    conc: formatWithRounding(c, sigDigits, precDigits),
                    pooled: formatWithRounding(pooledSignals[i], sigDigits, precDigits),
                    cv: pooledCV[i] !== undefined ? formatWithRounding(pooledCV[i], cvSigDigits, cvPrecDigits) : "",
                    fittedConc: derivedConcs[i] !== undefined ? formatWithRounding(derivedConcs[i], sigDigits, precDigits) : "",
                    fittedSignal: fittedSignals[i] !== undefined ? formatWithRounding(fittedSignals[i], sigDigits, precDigits) : null
                };
            });

            while (mgccData.length < 10) {
                mgccData.push({
                    id: mgccData.length,
                    ref: mgccData.length + 1,
                    conc: null,
                    pooled: null,
                    cv: null,
                    fittedConc: null,
                    fittedSignal: null
                });
            }

            setMgccRows(mgccData);

            // ----- Mirror unknown evaluations from the backend -----
            // Single-RCC mode only: master-curve fits never send unknowns,
            // so server response will not have unknown_* keys to mirror.
            // Index i in unknown_doses_from_alu matches index i in the
            // unknownAlus we sent.  We clear the cell for any row whose
            // input was blank or non-numeric.
            if (!isMaster) {
                const unkDoseOut = Array.isArray(data?.unknown_doses_from_alu) ? data.unknown_doses_from_alu : [];
                const unkAluOut = Array.isArray(data?.unknown_alus_from_dose) ? data.unknown_alus_from_dose : [];

                setUnknownAluRows(prev => prev.map((r, i) => {
                    if (unknownAlus[i] === null) return { ...r, fittedDose: "" };
                    const v = unkDoseOut[i];
                    if (v === undefined || v === null || !Number.isFinite(Number(v))) {
                        return { ...r, fittedDose: "" };
                    }
                    return { ...r, fittedDose: formatWithRounding(v, sigDigits, precDigits) };
                }));

                setUnknownDoseRows(prev => prev.map((r, i) => {
                    if (unknownDoses[i] === null) return { ...r, fittedAlu: "" };
                    const v = unkAluOut[i];
                    if (v === undefined || v === null || !Number.isFinite(Number(v))) {
                        return { ...r, fittedAlu: "" };
                    }
                    return { ...r, fittedAlu: formatWithRounding(v, sigDigits, precDigits) };
                }));
            }

            const unkAluCount = unknownAlus.filter(v => v !== null).length;
            const unkDoseCount = unknownDoses.filter(v => v !== null).length;
            const unkSummary = !isMaster && (unkAluCount || unkDoseCount)
                ? ` Unknowns: ${unkAluCount} ALU -> Dose, ${unkDoseCount} Dose -> ALU.`
                : "";
            setActiveFitLabel(fitLabel);
            setLastOperation(isMaster
                ? `Master Curve Generation (${numRCCs} RCC${numRCCs === 1 ? "" : "s"})`
                : `RCC-${selectedRccTab} Generation`);
            // Record the pool so a follow-up Calculate IGCC / Find Unknowns
            // re-uses the same scope. Master mode snapshots the excluded
            // RCCs at this moment so later checkbox edits don't quietly
            // change which pool the IGCC bend is anchored to.
            if (isMaster) {
                const excluded = [];
                for (let i = 0; i < visibleRccCount; i++) {
                    if (!selectedRCCs[i]) excluded.push(i + 1);
                }
                setLastMasterPool({ mode: "master", excluded });
            } else {
                setLastMasterPool({ mode: "single", rcc: selectedRccTab });
                // Mark this RCC's single-column fit as established so
                // Find Unknowns for this RCC is unlocked.
                setPerRccFitDone(prev => ({ ...prev, [selectedRccTab]: true }));
            }
            finishOperation(isMaster
                ? `Master curve generation complete: ${concs.length} levels across ${numRCCs} RCC curves.`
                : `RCC-${selectedRccTab} generation complete: ${concs.length} levels.${unkSummary}`,
                "success");
        } catch (err) {
            console.error(err);

            if (err?.code === "ERR_NETWORK") {
                showError("Connection Error", `Could not reach backend at ${RAWCURVE_POOLED_URL}. Make sure the backend or Docker container is running.`);
                finishOperation("Generate failed: backend connection error.", "error");
                return;
            }

            showError("Request Failed", err?.response?.data?.error || err?.message || "An unexpected error occurred.");
            finishOperation("Generate failed due to request error.", "error");
        }
    };

    // Authoritative IGCC re-calibration + Unknown evaluation for the
    // selected RCC tab.
    //
    // The pool fed into the underlying master fit is whatever the user last
    // established via Generate MGCC or Generate Master Curve:
    //   - Single-RCC Generate: pool = just that RCC's column.
    //   - Master-curve Generate: pool = all enabled RCCs at that moment
    //     (excluded RCCs snapshot-captured in lastMasterPool.excluded).
    // The IGCC and Unknown inputs are always taken from the currently
    // selected tab regardless of pool scope - exclusion from the master
    // pool does not prevent you from bending that RCC's IGCC against the
    // pooled curve.
    const handleCalculateIgcc = async () => {
        const fitLabel = `RCC-${selectedRccTab}`;
        if (!lastMasterPool) {
            showError(
                "No Master Curve",
                "Run Generate Master Curve or Generate MGCC first - the IGCC bend needs a fitted master curve to anchor against."
            );
            finishOperation(`Calculate IGCC (${fitLabel}) failed: no master curve has been generated.`, "error");
            return;
        }

        // Build the pool exactly as it was when the last Generate ran.
        // Master mode: re-derive enabled RCCs but force-exclude the ones
        // that were excluded at Generate-time (so re-ticking a checkbox
        // doesn't silently widen the pool under the user). Single mode:
        // narrow to that one RCC.
        let rccDataResult;
        let poolDescription;
        if (lastMasterPool.mode === "single") {
            rccDataResult = buildRccData({ rccFilter: lastMasterPool.rcc });
            poolDescription = `RCC-${lastMasterPool.rcc} only`;
        } else {
            const excludedSet = new Set(lastMasterPool.excluded || []);
            const enabled = [];
            for (let i = 0; i < visibleRccCount; i++) {
                if (selectedRCCs[i] && !excludedSet.has(i + 1)) enabled.push(i + 1);
            }
            rccDataResult = buildRccData({ rccSet: enabled });
            poolDescription = `Master Curve (${enabled.length} RCC${enabled.length === 1 ? "" : "s"})`;
        }
        startOperation(`Calculating IGCC adjustment for ${fitLabel} against ${poolDescription}...`);

        const { concs, rccSignals, numRCCs, missing } = rccDataResult;
        if (missing.length > 0) {
            const detail = formatMissingCells(missing);
            showError(
                "RCC Data Incomplete",
                `${missing.length} row${missing.length === 1 ? " has" : "s have"} missing cell${missing.length === 1 ? "" : "s"}. Fill every cell in each populated row before calculating IGCC, or clear the row entirely.\n\n${detail}`
            );
            finishOperation(`Calculate IGCC (${fitLabel}) failed: ${missing.length} row${missing.length === 1 ? "" : "s"} with missing cells.`, "error");
            return;
        }
        if (concs.length === 0 || numRCCs < 1) {
            showError("No RCC Data", `${poolDescription} has no concentration/signal data. Re-run Generate before calculating IGCC.`);
            finishOperation(`Calculate IGCC (${fitLabel}) failed: no RCC data available.`, "error");
            return;
        }
        if (concs.length < 5) {
            showError("Insufficient Data", `Only ${concs.length} valid concentration levels found in ${poolDescription}. At least 5 are required.`);
            finishOperation(`Calculate IGCC (${fitLabel}) failed: only ${concs.length} valid levels.`, "error");
            return;
        }

        const trendCheck = validateRccData(concs, rccSignals);
        if (!trendCheck.ok) {
            showError("RCC Data Inconsistent", trendCheck.message);
            finishOperation(`Calculate IGCC (${fitLabel}) failed: ${trendCheck.message}`, "error");
            return;
        }

        // Collect IGCC re-calibrator rows where BOTH ALU and Dose are filled.
        const igccPairs = igccRows
            .map(r => ({ alu: parseMaybeNumber(r.inputAlu), dose: parseMaybeNumber(r.inputDose) }))
            .filter(p => p.alu !== null && p.dose !== null);
        const igccConcs = igccPairs.map(p => p.dose);
        const igccSignals = igccPairs.map(p => p.alu);

        const unknownAlus = unknownAluRows.map(r => parseMaybeNumber(r.inputAlu));
        const unknownDoses = unknownDoseRows.map(r => parseMaybeNumber(r.inputDose));

        const haveIgcc = igccPairs.length > 0;
        const haveUnkAlu = unknownAlus.some(v => v !== null);
        const haveUnkDose = unknownDoses.some(v => v !== null);
        if (!haveIgcc && !haveUnkAlu && !haveUnkDose) {
            // Nothing to do that Generate MGCC didn't already do.  Tell the
            // user instead of silently re-running the same fit.
            const partial = igccRows.filter(r => {
                const a = parseMaybeNumber(r.inputAlu);
                const d = parseMaybeNumber(r.inputDose);
                return (a !== null) !== (d !== null); // exactly one of the two filled
            }).length;
            const detail = partial > 0
                ? ` Note: ${partial} IGCC row${partial === 1 ? " has" : "s have"} only ALU or only Dose filled - both are required per row.`
                : "";
            showError("Nothing to Calculate", `Enter at least one IGCC re-calibrator pair (both ALU and Dose) or one unknown sample value before clicking Calculate IGCC. Generate MGCC already evaluates unknowns on the plain master curve.${detail}`);
            finishOperation("Calculate IGCC skipped: no IGCC or unknown inputs.", "warning");
            return;
        }

        const payload = {
            assay: 1,
            concs,
            rccSignals,
            igccConcs,
            igccSignals,
            unknownAlus: unknownAlus.map(v => (v === null ? NaN : v)),
            unknownDoses: unknownDoses.map(v => (v === null ? NaN : v))
        };

        try {
            const response = await axios.post(
                RAWCURVE_IGCC_URL,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            const data = response.data;
            if (data?.status !== 1) {
                const msg = describeG06Status(data?.status, data?.message);
                // Surface the M_NO_DERIVE_CAL_REF-specific title when the
                // backend flagged that path; otherwise fall back to the
                // generic IGCC heading since the user clicked Calculate IGCC.
                const title = String(data?.status) === "20"
                    ? titleForG06Status(data?.status)
                    : "IGCC Calculation Failed";
                showError(title, msg);
                finishOperation(`Calculate IGCC failed: backend status ${data?.status ?? "unknown"} - ${msg}`, "error");
                return;
            }

            const sigDigits = rounding.significantDigits;
            const precDigits = rounding.precisionDigits;
            const cvSigDigits = rounding.cvSignificantDigits;
            const cvPrecDigits = rounding.cvPrecisionDigits;

            // ----- Update unknowns from authoritative xconc/ysignal -----
            const unkDoseOut = Array.isArray(data?.unknown_doses_from_alu) ? data.unknown_doses_from_alu : [];
            const unkAluOut = Array.isArray(data?.unknown_alus_from_dose) ? data.unknown_alus_from_dose : [];

            setUnknownAluRows(prev => prev.map((r, i) => {
                if (unknownAlus[i] === null) return { ...r, fittedDose: "" };
                const v = unkDoseOut[i];
                if (v === undefined || v === null || !Number.isFinite(Number(v))) {
                    return { ...r, fittedDose: "" };
                }
                return { ...r, fittedDose: formatWithRounding(v, sigDigits, precDigits) };
            }));

            setUnknownDoseRows(prev => prev.map((r, i) => {
                if (unknownDoses[i] === null) return { ...r, fittedAlu: "" };
                const v = unkAluOut[i];
                if (v === undefined || v === null || !Number.isFinite(Number(v))) {
                    return { ...r, fittedAlu: "" };
                }
                return { ...r, fittedAlu: formatWithRounding(v, sigDigits, precDigits) };
            }));

            // Refresh the master-curve display from the response. The backend
            // re-fits on every call (twice when IGCC pairs are supplied via
            // derive_cal_ref + scale_mgs_data + MGSC_curve_fit), so params,
            // curve_points, derived_concs and fitted_signals always reflect
            // the active curve and must be applied even when igcc_applied is
            // false.
            const params = data?.params;
            if (Array.isArray(params) && params.length >= 5) {
                setAbcdn({
                    a: formatWithRounding(params[0], sigDigits, precDigits),
                    b: formatWithRounding(params[1], sigDigits, precDigits),
                    c: formatWithRounding(params[2], sigDigits, precDigits),
                    d: formatWithRounding(params[3], sigDigits, precDigits),
                    n: formatWithRounding(params[4], sigDigits, precDigits)
                });
            }
            if (data?.fit_factor !== undefined && data?.fit_factor !== null) {
                setFitFactor(formatWithRounding(data.fit_factor, sigDigits, precDigits));
            }
            // Shape factor: prefer the IGCC piecewise mismatch when IGCC was
            // applied (this is what tells the user how far their calibrator
            // pairs pulled the curve away from the plain RCC fit); otherwise
            // fall back to the standard MGCC shape_factor.
            if (data?.igcc_applied && data?.igcc_shape_factor !== undefined && data?.igcc_shape_factor !== null) {
                setShapeFactor(formatWithRounding(data.igcc_shape_factor, sigDigits, precDigits));
            } else if (data?.shape_factor !== undefined && data?.shape_factor !== null) {
                setShapeFactor(formatWithRounding(data.shape_factor, sigDigits, precDigits));
            }

            const curvePts = Array.isArray(data?.curve_points)
                ? data.curve_points
                    .map((p) => ({ x: Number(p.x), y: Number(p.y) }))
                    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
                : [];
            if (curvePts.length > 0) {
                setGraphLinePoints(curvePts);
            }

            // Refresh the red "Raw Data" markers from the pooled signals the
            // backend used.  These do NOT change with IGCC (they come from
            // the original RCC input), but updating from the authoritative
            // server response keeps everything in lock-step.
            const pooledSignals = data?.pooled_signals || {};
            const pooledCV = data?.pooled_cv || {};
            const derivedConcs = data?.derived_concs || {};
            const fittedSignals = data?.fitted_signals || {};

            setGraphRawPoints(concs.map((c, i) => ({
                x: c,
                y: pooledSignals[i] || 0
            })));

            // Rebuild the MGCC table.  pooled / %CV stay constant (input-
            // derived); fittedConc and fittedSignal are recomputed from the
            // post-IGCC curve and SHOULD shift visibly when IGCC pairs move
            // the curve.
            const mgccData = concs.map((c, i) => ({
                id: i,
                ref: i + 1,
                conc: formatWithRounding(c, sigDigits, precDigits),
                pooled: formatWithRounding(pooledSignals[i], sigDigits, precDigits),
                cv: pooledCV[i] !== undefined ? formatWithRounding(pooledCV[i], cvSigDigits, cvPrecDigits) : "",
                fittedConc: derivedConcs[i] !== undefined ? formatWithRounding(derivedConcs[i], sigDigits, precDigits) : "",
                fittedSignal: fittedSignals[i] !== undefined ? formatWithRounding(fittedSignals[i], sigDigits, precDigits) : null
            }));
            while (mgccData.length < 10) {
                mgccData.push({
                    id: mgccData.length,
                    ref: mgccData.length + 1,
                    conc: null,
                    pooled: null,
                    cv: null,
                    fittedConc: null,
                    fittedSignal: null
                });
            }
            setMgccRows(mgccData);
            setResultOpen(true);

            const igccCount = igccPairs.length;
            const unkAluCount = unknownAlus.filter(v => v !== null).length;
            const unkDoseCount = unknownDoses.filter(v => v !== null).length;
            const adjMsg = data?.igcc_applied
                ? `${fitLabel} IGCC bent against ${poolDescription} (${igccCount} re-calibrator${igccCount === 1 ? "" : "s"}, shape=${formatWithRounding(data?.igcc_shape_factor ?? 0, sigDigits, precDigits)})`
                : `IGCC not applied to ${fitLabel} against ${poolDescription} (${igccCount} re-calibrator${igccCount === 1 ? "" : "s"} provided)`;
            setActiveFitLabel(data?.igcc_applied
                ? `${fitLabel} IGCC bent against ${poolDescription}`
                : `${fitLabel} (IGCC not applied)`);
            setLastOperation(data?.igcc_applied
                ? `${fitLabel} IGCC Re-calibration vs ${poolDescription} (${igccCount} pair${igccCount === 1 ? "" : "s"})`
                : `${fitLabel} IGCC skipped vs ${poolDescription}`);
            finishOperation(`${adjMsg}; unknowns: ${unkAluCount} ALU -> Dose, ${unkDoseCount} Dose -> ALU.`, data?.igcc_applied ? "success" : "error");
        } catch (err) {
            console.error(err);
            if (err?.code === "ERR_NETWORK") {
                showError("Connection Error", `Could not reach backend at ${RAWCURVE_IGCC_URL}. Make sure the backend or Docker container is running.`);
                finishOperation("Calculate IGCC failed: backend connection error.", "error");
                return;
            }
            showError("Request Failed", err?.response?.data?.error || err?.message || "An unexpected error occurred.");
            finishOperation("Calculate IGCC failed due to request error.", "error");
        }
    };

    // -----------------------------------------------------------------------
    // Find Unknowns: re-evaluate the unknown panels against whatever fitted
    // curve is currently appropriate for the entered data, without disturbing
    // the user's setup. The backend always returns xconc()/ysignal() values
    // alongside its fit, so we just resend:
    //   - the same RCC matrix (required for any fit),
    //   - any IGCC re-calibrator pairs already entered (so the active curve
    //     matches what the user last saw - bent if IGCC pairs were filled,
    //     plain RCC fit otherwise),
    //   - the unknown ALU and Dose columns.
    // No master-curve / abcdn state is overwritten here; we only refresh the
    // unknown row outputs.  This is the lightweight "evaluate on the existing
    // curve" path the user wants.
    // -----------------------------------------------------------------------
    const handleFindUnknowns = async () => {
        const fitLabel = `RCC-${selectedRccTab}`;
        // Find Unknowns is scoped per RCC and uses only that RCC's column
        // for the pool. It requires the user to have first run
        // Generate MGCC for this specific RCC (not Generate Master Curve)
        // - unknowns belong to a single RCC's curve, not the cross-RCC
        // master.
        if (!perRccFitDone[selectedRccTab]) {
            showError(
                "RCC Not Generated",
                `Run Generate MGCC (RCC-${selectedRccTab}) first - unknowns are evaluated against this RCC's own 1-column curve and need a fit to be established.`
            );
            finishOperation(`Find Unknowns (${fitLabel}) failed: RCC-${selectedRccTab} has not been generated yet.`, "error");
            return;
        }

        const poolDescription = `RCC-${selectedRccTab} only`;
        const rccDataResult = buildRccData({ rccFilter: selectedRccTab });
        startOperation(`Finding fitted unknowns for ${fitLabel}...`);

        const { concs, rccSignals, numRCCs, missing } = rccDataResult;
        if (missing.length > 0) {
            const detail = formatMissingCells(missing);
            showError(
                "RCC Data Incomplete",
                `${missing.length} row${missing.length === 1 ? " has" : "s have"} missing cell${missing.length === 1 ? "" : "s"}. Fill every cell in each populated row before finding unknowns, or clear the row entirely.\n\n${detail}`
            );
            finishOperation(`Find Unknowns (${fitLabel}) failed: ${missing.length} row${missing.length === 1 ? "" : "s"} with missing cells.`, "error");
            return;
        }
        if (concs.length === 0 || numRCCs < 1) {
            showError("No RCC Data", `${poolDescription} has no concentration/signal data. Re-run Generate MGCC for this RCC first.`);
            finishOperation(`Find Unknowns (${fitLabel}) failed: no RCC data.`, "error");
            return;
        }
        if (concs.length < 5) {
            showError("Insufficient Data", `Only ${concs.length} valid concentration levels found in ${poolDescription}. At least 5 are required for a fit.`);
            finishOperation(`Find Unknowns (${fitLabel}) failed: only ${concs.length} valid levels.`, "error");
            return;
        }

        const trendCheck = validateRccData(concs, rccSignals);
        if (!trendCheck.ok) {
            showError("RCC Data Inconsistent", trendCheck.message);
            finishOperation(`Find Unknowns (${fitLabel}) failed: ${trendCheck.message}`, "error");
            return;
        }

        const unknownAlus = unknownAluRows.map(r => parseMaybeNumber(r.inputAlu));
        const unknownDoses = unknownDoseRows.map(r => parseMaybeNumber(r.inputDose));
        const haveUnkAlu = unknownAlus.some(v => v !== null);
        const haveUnkDose = unknownDoses.some(v => v !== null);
        if (!haveUnkAlu && !haveUnkDose) {
            showError("No Unknowns Entered", "Type or import at least one ALU or Dose value into the Unknowns panels before clicking Find Unknowns.");
            finishOperation("Find Unknowns skipped: no unknown inputs.", "warning");
            return;
        }

        // Preserve whichever curve the user last saw: include IGCC pairs if
        // they have already been filled in, so the backend re-applies the
        // same adjustment before evaluating xconc()/ysignal().
        const igccPairs = igccRows
            .map(r => ({ alu: parseMaybeNumber(r.inputAlu), dose: parseMaybeNumber(r.inputDose) }))
            .filter(p => p.alu !== null && p.dose !== null);

        const payload = {
            assay: 1,
            concs,
            rccSignals,
            igccConcs: igccPairs.map(p => p.dose),
            igccSignals: igccPairs.map(p => p.alu),
            unknownAlus: unknownAlus.map(v => (v === null ? NaN : v)),
            unknownDoses: unknownDoses.map(v => (v === null ? NaN : v))
        };

        try {
            const response = await axios.post(
                RAWCURVE_POOLED_URL,
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            const data = response.data;
            if (data?.status !== 1) {
                const msg = describeG06Status(data?.status, data?.message);
                showError(`Find Unknowns: ${titleForG06Status(data?.status)}`, msg);
                finishOperation(`Find Unknowns failed: backend status ${data?.status ?? "unknown"} - ${msg}`, "error");
                return;
            }

            const sigDigits = rounding.significantDigits;
            const precDigits = rounding.precisionDigits;

            const unkDoseOut = Array.isArray(data?.unknown_doses_from_alu) ? data.unknown_doses_from_alu : [];
            const unkAluOut = Array.isArray(data?.unknown_alus_from_dose) ? data.unknown_alus_from_dose : [];

            setUnknownAluRows(prev => prev.map((r, i) => {
                if (unknownAlus[i] === null) return { ...r, fittedDose: "" };
                const v = unkDoseOut[i];
                if (v === undefined || v === null || !Number.isFinite(Number(v))) {
                    return { ...r, fittedDose: "" };
                }
                return { ...r, fittedDose: formatWithRounding(v, sigDigits, precDigits) };
            }));

            setUnknownDoseRows(prev => prev.map((r, i) => {
                if (unknownDoses[i] === null) return { ...r, fittedAlu: "" };
                const v = unkAluOut[i];
                if (v === undefined || v === null || !Number.isFinite(Number(v))) {
                    return { ...r, fittedAlu: "" };
                }
                return { ...r, fittedAlu: formatWithRounding(v, sigDigits, precDigits) };
            }));

            const unkAluCount = unknownAlus.filter(v => v !== null).length;
            const unkDoseCount = unknownDoses.filter(v => v !== null).length;
            const bendNote = data?.igcc_applied ? " + IGCC bend" : "";
            finishOperation(`Find Unknowns for ${fitLabel} complete against ${poolDescription}${bendNote}: ${unkAluCount} ALU -> Dose, ${unkDoseCount} Dose -> ALU.`, "success");
        } catch (err) {
            console.error(err);
            if (err?.code === "ERR_NETWORK") {
                showError("Connection Error", `Could not reach backend at ${RAWCURVE_POOLED_URL}. Make sure the backend or Docker container is running.`);
                finishOperation("Find Unknowns failed: backend connection error.", "error");
                return;
            }
            showError("Request Failed", err?.response?.data?.error || err?.message || "An unexpected error occurred.");
            finishOperation("Find Unknowns failed due to request error.", "error");
        }
    };

    // ---------- helpers used by exportToExcel ----------

    // Rasterize the live SVG chart to a PNG Blob.  Uses a base64 data: URL
    // (instead of a blob: URL) for the intermediate Image because some
    // Chromium-derived browsers refuse to rasterize a blob:-served SVG that
    // contains <text> elements.
    // Rasterize an SVG to a PNG Blob.  Accepts either an existing <svg>
    // element (used for the live on-page chart, rasterized for the
    // currently-viewed fit) or a self-contained SVG string (used to embed
    // per-RCC and per-IGCC charts that aren't mounted in the DOM).
    // Uses a base64 data: URL for the intermediate Image because some
    // Chromium-derived browsers refuse to rasterize a blob:-served SVG
    // that contains <text> elements.
    const rasterizeSvgToPng = (svgSource) => new Promise((resolve, reject) => {
        let svgStr;
        if (typeof svgSource === "string") {
            svgStr = '<?xml version="1.0" standalone="no"?>\r\n' + svgSource;
        } else if (svgSource && typeof svgSource.cloneNode === "function") {
            const clone = svgSource.cloneNode(true);
            clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
            clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
            clone.setAttribute("width", "874");
            clone.setAttribute("height", "633");
            clone.setAttribute("viewBox", "0 0 874 633");
            svgStr = '<?xml version="1.0" standalone="no"?>\r\n' +
                new XMLSerializer().serializeToString(clone);
        } else {
            return reject(new Error("SVG source is null"));
        }
        const dataUri = "data:image/svg+xml;charset=utf-8;base64," +
            btoa(unescape(encodeURIComponent(svgStr)));
        const img = new Image();
        img.width = 874;
        img.height = 633;
        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = 874;
                canvas.height = 633;
                const ctx = canvas.getContext("2d");
                ctx.fillStyle = "#fafafa";
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, 874, 633);
                canvas.toBlob((blob) => {
                    if (blob) resolve(blob);
                    else reject(new Error("canvas.toBlob returned null"));
                }, "image/png");
            } catch (e) {
                reject(e);
            }
        };
        img.onerror = (e) => reject(e);
        img.src = dataUri;
    });

    // Backwards-compatible wrapper used by the existing "currently-viewed
    // fit" embed path: rasterizes the live on-page SVG via its ref.
    const generatePngBlob = () => rasterizeSvgToPng(svgRef.current);

    // Build a standalone SVG string that visually matches the on-page chart
    // (same dimensions, padding, axis ticks, legend, marker styles). Used to
    // embed per-RCC MGCC and IGCC charts into the Excel export without
    // needing those fits to be mounted in the DOM. linePts / rawPts /
    // igccPts use the same shape the on-page renderer uses: arrays of
    // { x, y }. title is rendered at top center; subtitle (e.g. "RCC-7 with
    // IGCC bend") appears below it in smaller text.
    const buildChartSvgString = ({ linePts = [], rawPts = [], igccPts = [], title = chartTitle, subtitle = "" } = {}) => {
        const all = [...linePts, ...rawPts, ...igccPts];
        if (all.length === 0) {
            return `<svg xmlns="http://www.w3.org/2000/svg" width="874" height="633" viewBox="0 0 874 633"><rect width="874" height="633" fill="#fafafa"/><text x="437" y="316" text-anchor="middle" font-size="14" fill="#999">No data to plot</text></svg>`;
        }
        const xs = all.map(p => p.x);
        const ys = all.map(p => p.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const leftPad = 70, rightPad = 20, topPad = 65, bottomPad = 50;
        const chartWidth = 874 - leftPad - rightPad;
        const chartHeight = 633 - topPad - bottomPad;
        const sx = (x) => leftPad + ((x - minX) / ((maxX - minX) || 1)) * chartWidth;
        const sy = (y) => topPad + chartHeight - ((y - minY) / ((maxY - minY) || 1)) * chartHeight;

        const linePoly = linePts.map(p => `${sx(p.x).toFixed(2)},${sy(p.y).toFixed(2)}`).join(" ");

        const xTicks = Array.from({ length: 5 }, (_, i) => {
            const value = minX + ((maxX - minX) * i) / 4;
            return { value, x: sx(value) };
        });
        const yTicks = Array.from({ length: 5 }, (_, i) => {
            const value = minY + ((maxY - minY) * (4 - i)) / 4;
            return { value, y: sy(value) };
        });

        const parts = [];
        parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="874" height="633" viewBox="0 0 874 633">`);
        parts.push(`<rect width="874" height="633" fill="#fafafa"/>`);

        // Title + subtitle (subtitle mirrors the on-page "Currently viewing"
        // banner so the embedded image carries the same context).
        parts.push(`<text x="${leftPad + chartWidth / 2}" y="50" text-anchor="middle" font-size="14" font-weight="bold" fill="#333">${escapeXml(title)}</text>`);
        if (subtitle) {
            parts.push(`<text x="${leftPad + chartWidth / 2}" y="22" text-anchor="middle" font-size="11" fill="#1976d2">${escapeXml(subtitle)}</text>`);
        }

        // Axes.
        parts.push(`<line x1="${leftPad}" y1="${topPad + chartHeight}" x2="${leftPad + chartWidth}" y2="${topPad + chartHeight}" stroke="#555"/>`);
        parts.push(`<line x1="${leftPad}" y1="${topPad}" x2="${leftPad}" y2="${topPad + chartHeight}" stroke="#555"/>`);

        // X ticks + labels.
        for (const t of xTicks) {
            parts.push(`<line x1="${t.x}" y1="${topPad + chartHeight}" x2="${t.x}" y2="${topPad + chartHeight + 5}" stroke="#555"/>`);
            parts.push(`<text x="${t.x}" y="${topPad + chartHeight + 17}" text-anchor="middle" font-size="10" fill="#333">${Number(t.value).toFixed(2)}</text>`);
        }
        // Y ticks + gridlines + labels.
        for (const t of yTicks) {
            parts.push(`<line x1="${leftPad - 5}" y1="${t.y}" x2="${leftPad}" y2="${t.y}" stroke="#555"/>`);
            parts.push(`<line x1="${leftPad}" y1="${t.y}" x2="${leftPad + chartWidth}" y2="${t.y}" stroke="#e0e0e0"/>`);
            parts.push(`<text x="${leftPad - 8}" y="${t.y + 3}" text-anchor="end" font-size="10" fill="#333">${Number(t.value).toFixed(2)}</text>`);
        }

        // Axis labels.
        parts.push(`<text x="${leftPad + chartWidth / 2}" y="${topPad + chartHeight + 40}" text-anchor="middle" font-size="12" font-weight="bold" fill="#333">Concentration</text>`);
        parts.push(`<text x="12" y="${topPad + chartHeight / 2}" text-anchor="middle" font-size="12" font-weight="bold" fill="#333" transform="rotate(-90 12 ${topPad + chartHeight / 2})">Signal</text>`);

        // Fitted curve polyline + dots.
        if (linePts.length > 0) {
            parts.push(`<polyline fill="none" stroke="#1976d2" stroke-width="2" points="${linePoly}"/>`);
            for (const p of linePts) {
                parts.push(`<circle cx="${sx(p.x).toFixed(2)}" cy="${sy(p.y).toFixed(2)}" r="3" fill="#1976d2" stroke="white" stroke-width="1.5"/>`);
            }
        }
        // Raw data red dots.
        for (const p of rawPts) {
            parts.push(`<circle cx="${sx(p.x).toFixed(2)}" cy="${sy(p.y).toFixed(2)}" r="4" fill="#d32f2f"/>`);
        }
        // IGCC pseudocal purple crosses (same arm length as on-page chart).
        const arm = 6;
        for (const p of igccPts) {
            const cx = sx(p.x), cy = sy(p.y);
            parts.push(`<line x1="${(cx - arm).toFixed(2)}" y1="${cy.toFixed(2)}" x2="${(cx + arm).toFixed(2)}" y2="${cy.toFixed(2)}" stroke="#7b1fa2" stroke-width="2.25" stroke-linecap="round"/>`);
            parts.push(`<line x1="${cx.toFixed(2)}" y1="${(cy - arm).toFixed(2)}" x2="${cx.toFixed(2)}" y2="${(cy + arm).toFixed(2)}" stroke="#7b1fa2" stroke-width="2.25" stroke-linecap="round"/>`);
        }

        // Legend (only includes Pseudocal row when IGCC points are present).
        const legendX = leftPad + chartWidth - 130;
        const legendY = topPad + 6;
        const rowH = 16;
        const showPseudocal = igccPts.length > 0;
        const legendRows = showPseudocal ? 3 : 2;
        parts.push(`<rect x="${legendX - 8}" y="${legendY - 4}" width="130" height="${rowH * legendRows + 8}" fill="#ffffff" fill-opacity="0.85" stroke="#bbb" stroke-width="0.5" rx="3"/>`);
        parts.push(`<line x1="${legendX}" y1="${legendY + 6}" x2="${legendX + 18}" y2="${legendY + 6}" stroke="#1976d2" stroke-width="2"/>`);
        parts.push(`<circle cx="${legendX + 9}" cy="${legendY + 6}" r="3" fill="#1976d2"/>`);
        parts.push(`<text x="${legendX + 24}" y="${legendY + 9}" font-size="11" fill="#333">Fitted Curve</text>`);
        parts.push(`<circle cx="${legendX + 9}" cy="${legendY + rowH + 6}" r="4" fill="#d32f2f"/>`);
        parts.push(`<text x="${legendX + 24}" y="${legendY + rowH + 9}" font-size="11" fill="#333">Raw Data</text>`);
        if (showPseudocal) {
            parts.push(`<line x1="${legendX + 3}" y1="${legendY + rowH * 2 + 6}" x2="${legendX + 15}" y2="${legendY + rowH * 2 + 6}" stroke="#7b1fa2" stroke-width="2.25" stroke-linecap="round"/>`);
            parts.push(`<line x1="${legendX + 9}" y1="${legendY + rowH * 2}" x2="${legendX + 9}" y2="${legendY + rowH * 2 + 12}" stroke="#7b1fa2" stroke-width="2.25" stroke-linecap="round"/>`);
            parts.push(`<text x="${legendX + 24}" y="${legendY + rowH * 2 + 9}" font-size="11" fill="#333">Pseudocal</text>`);
        }

        parts.push(`</svg>`);
        return parts.join("");
    };

    // Minimal XML escaper used by buildChartSvgString for text content.
    const escapeXml = (s) => String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");

    const blobToArrayBuffer = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });

    // Build a real .xlsx workbook (via ExcelJS) laid out as a 2-row grid:
    //   row 1  - input tables side-by-side: RCC | IGCC | Unknown ALU | Unknown Dose
    //   row 2  - results: MGCC table | embedded chart | curve points (to right of chart)
    // Sections are written at explicit (row, col) coordinates rather than
    // appended linearly so the document stays wider-than-tall.  The chart
    // is embedded as a cell-anchored image, so it scales with row/column
    // resizes the same way Excel's "Move and size with cells" option does.
    // Saved via file-saver as G06_Export_<timestamp>.xlsx.
    // Build a multi-sheet .xlsx export.
    //   Sheet 1 ("Master Curve") - pool of every currently-enabled RCC,
    //                              fit data only (no IGCC, no unknowns).
    //   Sheets 2..N+1 ("RCC-1" ..) - one per visible RCC, each with its
    //                                own 1-column fit, that tab's IGCC,
    //                                unknown ALU table, unknown Dose table,
    //                                and curve points.
    // Per-sheet fit data is fetched at export-time via one /fitpooled
    // request per sheet, so each RCC's curve / params / shape factor are
    // accurate even if the user never clicked Generate on that tab.
    // Sections are written at explicit (row, col) coordinates so the
    // document stays wider-than-tall. The live SVG chart is embedded only
    // on the sheet that matches the currently-viewed fit (rasterizing 24
    // offscreen SVGs is not practical client-side); other sheets get the
    // curve-points table so the reviewer can rebuild a chart in Excel.
    // Saved via file-saver as G06_Export_<timestamp>.xlsx.
    const exportToExcel = async () => {
        try {
            const exportedAt = new Date();
            const stampDisplay = exportedAt.toLocaleString();
            const stampForFile = exportedAt.toISOString().replace(/[:.]/g, "-").slice(0, 19);

            const wb = new ExcelJS.Workbook();
            wb.creator = "G06 MGCC Generation";
            wb.created = exportedAt;

            // ---------- helpers ----------
            // Write a labelled block of rows starting at (startRow, startCol)
            // on a given worksheet. Title row (bold) and header row (bold)
            // are both optional. Returns the next-available row below the
            // block.
            const writeBlock = (ws, startRow, startCol, title, headers, rows) => {
                let r = startRow;
                if (title) {
                    const c = ws.getCell(r, startCol);
                    c.value = title;
                    c.font = { bold: true };
                    r++;
                }
                if (headers) {
                    for (let i = 0; i < headers.length; i++) {
                        const c = ws.getCell(r, startCol + i);
                        c.value = headers[i];
                        c.font = { bold: true };
                    }
                    r++;
                }
                for (const row of rows) {
                    for (let i = 0; i < row.length; i++) {
                        ws.getCell(r, startCol + i).value = row[i];
                    }
                    r++;
                }
                return r;
            };

            const visAnalyzerIds = rccAnalyzerIds.slice(0, visibleRccCount);
            const visCalIds = rccIds.slice(0, visibleRccCount);

            // Fire a backend fit request and parse the response into the
            // same shape the on-page state would carry, so per-sheet rendering
            // can reuse one writer regardless of which RCCs were pooled.
            const sigDigits = rounding.significantDigits;
            const precDigits = rounding.precisionDigits;
            const cvSigDigits = rounding.cvSignificantDigits;
            const cvPrecDigits = rounding.cvPrecisionDigits;
            const fmt = (v) => formatWithRounding(v, sigDigits, precDigits);
            const fmtCV = (v) => formatWithRounding(v, cvSigDigits, cvPrecDigits);

            const runFit = async ({ rccFilter = null, rccSet = null, igccPairs = [], unknownAlus = [], unknownDoses = [] }) => {
                const { concs, rccSignals, numRCCs } = buildRccData({ rccFilter, rccSet });
                if (concs.length === 0 || numRCCs < 1) {
                    return { ok: false, reason: "no data" };
                }
                const payload = {
                    assay: 1,
                    concs,
                    rccSignals,
                    igccConcs: igccPairs.map(p => p.dose),
                    igccSignals: igccPairs.map(p => p.alu),
                    unknownAlus: unknownAlus.map(v => (v === null ? NaN : v)),
                    unknownDoses: unknownDoses.map(v => (v === null ? NaN : v))
                };
                // Single retry on transient network errors so a momentary
                // blip during the 25-call export doesn't cascade into 25
                // failed sheets. Backoff between attempts; remote backend
                // CORS preflight can briefly rate-limit a burst of calls
                // from the same origin.
                let lastErr = null;
                for (let attempt = 0; attempt < 2; attempt++) {
                    try {
                        const response = await axios.post(
                            RAWCURVE_POOLED_URL, payload,
                            { headers: { "Content-Type": "application/json" }, timeout: 30000 }
                        );
                        const data = response.data;
                        if (data?.status !== 1) {
                            return { ok: false, reason: `backend status ${data?.status} - ${describeG06Status(data?.status, data?.message)}` };
                        }
                        return {
                            ok: true,
                            concs,
                            numRCCs,
                            params: Array.isArray(data?.params) && data.params.length >= 5 ? data.params : null,
                            fitFactor: data?.fit_factor ?? null,
                            shapeFactor: (data?.igcc_applied && data?.igcc_shape_factor != null)
                                ? data.igcc_shape_factor
                                : (data?.shape_factor ?? null),
                            pooledSignals: data?.pooled_signals || {},
                            pooledCV: data?.pooled_cv || {},
                            derivedConcs: data?.derived_concs || {},
                            fittedSignals: data?.fitted_signals || {},
                            curvePoints: Array.isArray(data?.curve_points)
                                ? data.curve_points
                                    .map(p => ({ x: Number(p.x), y: Number(p.y) }))
                                    .filter(p => Number.isFinite(p.x) && Number.isFinite(p.y))
                                : [],
                            unknownDosesFromAlu: Array.isArray(data?.unknown_doses_from_alu) ? data.unknown_doses_from_alu : [],
                            unknownAlusFromDose: Array.isArray(data?.unknown_alus_from_dose) ? data.unknown_alus_from_dose : [],
                            igccApplied: !!data?.igcc_applied
                        };
                    } catch (err) {
                        lastErr = err;
                        // Only retry on connection-class failures, not on
                        // 4xx/5xx where the backend rejected the payload.
                        const transient = err?.code === "ERR_NETWORK" || err?.code === "ECONNABORTED" || !err?.response;
                        if (!transient || attempt === 1) break;
                        await new Promise(r => setTimeout(r, 400));
                    }
                }
                return { ok: false, reason: lastErr?.message || "request failed" };
            };

            // Renders the standard top-of-sheet metadata strip onto a
            // worksheet. Always the same shape so reviewers can navigate
            // between sheets without re-orienting.
            const writeHeader = (ws, sheetLabel, fitContext) => {
                ws.getCell(1, 1).value = "Exported:"; ws.getCell(1, 1).font = { bold: true };
                ws.getCell(1, 2).value = stampDisplay;
                ws.getCell(1, 4).value = "Sheet:"; ws.getCell(1, 4).font = { bold: true };
                ws.getCell(1, 5).value = sheetLabel;
                ws.getCell(1, 7).value = "Pool:"; ws.getCell(1, 7).font = { bold: true };
                ws.getCell(1, 8).value = fitContext;

                ws.getCell(3, 1).value = "Analyte Code:"; ws.getCell(3, 2).value = metadata.analyteCode;
                ws.getCell(3, 4).value = "Assay Name:";   ws.getCell(3, 5).value = metadata.assayName;
                ws.getCell(3, 7).value = "Reagent Lot:";  ws.getCell(3, 8).value = metadata.reagentLot;
                ws.getCell(3, 10).value = "MGCC Id:";     ws.getCell(3, 11).value = metadata.mgccId;

                ws.getCell(4, 1).value = "Protocol Id:";  ws.getCell(4, 2).value = metadata.protocolId;
                ws.getCell(4, 4).value = "Protocol #:";   ws.getCell(4, 5).value = metadata.protocolNumber;
                ws.getCell(4, 7).value = "Owner:";        ws.getCell(4, 8).value = metadata.owner;
                ws.getCell(4, 10).value = "Date:";        ws.getCell(4, 11).value = metadata.date;
                ws.getCell(4, 13).value = "Gem ID:";      ws.getCell(4, 14).value = metadata.genId;
            };

            // Writes the RCC raw-data block (Ref + Concentration + N RCC
            // value columns). For per-RCC sheets only one column is shown.
            // Returns the row immediately after the block.
            const writeRccTable = (ws, startRow, rccNums) => {
                const headers = [
                    "Ref",
                    "Concentration",
                    ...rccNums.map(n => `RCC-${n}`)
                ];
                const rows = Array.from({ length: rccRowCount }, (_, i) => {
                    const src = gridData[i] || {};
                    const out = [i + 1, src.concentration ?? ""];
                    for (const n of rccNums) out.push(src[`RCC-${n}`] ?? "");
                    return out;
                });
                const nextRow = writeBlock(ws, startRow, 1, "RCC Data", headers, rows);
                return { nextRow, blockCols: 2 + rccNums.length };
            };

            // Renders an MGCC Results block + curve-points block + optional
            // IGCC and Unknowns blocks. fitContext describes the pool in
            // human-readable form (used in the header). Returns row offsets
            // so the caller can position the embedded chart if desired.
            const writeFitSection = (ws, startRow, fit, igccRowsForSheet, unkAluRowsForSheet, unkDoseRowsForSheet) => {
                // Fit Factor / Shape Factor / a-b-c-d-n strip.
                ws.getCell(startRow, 1).value = "Fit Factor"; ws.getCell(startRow, 1).font = { bold: true };
                ws.getCell(startRow, 2).value = fit.fitFactor != null ? fmt(fit.fitFactor) : "";
                ws.getCell(startRow, 4).value = "Shape Factor"; ws.getCell(startRow, 4).font = { bold: true };
                ws.getCell(startRow, 5).value = fit.shapeFactor != null ? fmt(fit.shapeFactor) : "";
                if (fit.params) {
                    ws.getCell(startRow + 1, 1).value = `a: ${fmt(fit.params[0])}`;
                    ws.getCell(startRow + 1, 2).value = `b: ${fmt(fit.params[1])}`;
                    ws.getCell(startRow + 1, 3).value = `c: ${fmt(fit.params[2])}`;
                    ws.getCell(startRow + 1, 4).value = `d: ${fmt(fit.params[3])}`;
                    ws.getCell(startRow + 1, 5).value = `n: ${fmt(fit.params[4])}`;
                }

                const mgccRowStart = startRow + 3;
                const mgccTableRows = fit.concs.map((c, i) => [
                    i + 1,
                    fmt(c),
                    fmt(fit.pooledSignals[i]),
                    fit.pooledCV[i] !== undefined ? fmtCV(fit.pooledCV[i]) : "",
                    fit.derivedConcs[i] !== undefined ? fmt(fit.derivedConcs[i]) : "",
                    fit.fittedSignals[i] !== undefined ? fmt(fit.fittedSignals[i]) : ""
                ]);
                writeBlock(
                    ws, mgccRowStart, 1,
                    "MGCC Results",
                    ["Ref", "Conc", "Pooled", "%CV", "Fit Conc", "Fit Signal"],
                    mgccTableRows
                );

                // Curve points to the right of where the chart sits.
                const curvePointsStartCol = 21;
                writeBlock(
                    ws, mgccRowStart, curvePointsStartCol,
                    "Curve Points",
                    ["Dose", "Signal"],
                    fit.curvePoints.map(p => [Number(p.x).toFixed(4), Number(p.y).toFixed(4)])
                );

                // Optional IGCC + Unknown sections sit below the MGCC table,
                // side-by-side on their own row. Only rendered when the
                // caller supplied non-empty arrays.
                const igccActive = Array.isArray(igccRowsForSheet) && igccRowsForSheet.some(r =>
                    parseMaybeNumber(r.inputAlu) !== null && parseMaybeNumber(r.inputDose) !== null
                );
                const unkAluActive = Array.isArray(unkAluRowsForSheet) && unkAluRowsForSheet.some(r => parseMaybeNumber(r.inputAlu) !== null);
                const unkDoseActive = Array.isArray(unkDoseRowsForSheet) && unkDoseRowsForSheet.some(r => parseMaybeNumber(r.inputDose) !== null);

                const inputsRow = mgccRowStart + 2 + mgccTableRows.length + 2;
                const GAP = 1;
                const igccCol = 1;
                const unkAluCol = igccCol + 3 + GAP;
                const unkDoseCol = unkAluCol + 3 + GAP;

                if (igccActive) {
                    writeBlock(
                        ws, inputsRow, igccCol,
                        "IGCC Re-Calibrators",
                        ["ID", "ALU", "Dose"],
                        igccRowsForSheet.map(r => [r.calId ?? "", r.inputAlu ?? "", r.inputDose ?? ""])
                    );
                }
                if (unkAluActive) {
                    writeBlock(
                        ws, inputsRow, unkAluCol,
                        "Unknowns: Input ALU -> Fitted Dose",
                        ["ID", "Input ALU", "Fitted Dose"],
                        unkAluRowsForSheet.map(r => [r.sampleId ?? "", r.inputAlu ?? "", r.fittedDose ?? ""])
                    );
                }
                if (unkDoseActive) {
                    writeBlock(
                        ws, inputsRow, unkDoseCol,
                        "Unknowns: Input Dose -> Fitted ALU",
                        ["ID", "Input Dose", "Fitted ALU"],
                        unkDoseRowsForSheet.map(r => [r.sampleId ?? "", r.inputDose ?? "", r.fittedAlu ?? ""])
                    );
                }

                return {
                    mgccRowStart,
                    curvePointsStartCol,
                    igccActive,
                    unkAluActive,
                    unkDoseActive
                };
            };

            // Apply consistent column widths.
            const applyColumnWidths = (ws, rccBlockCols, curvePointsStartCol) => {
                ws.getColumn(1).width = 14;
                ws.getColumn(2).width = 14;
                for (let c = 3; c <= rccBlockCols; c++) ws.getColumn(c).width = 9;
                for (let c = 1; c <= 9; c += 4) {
                    for (let off = 0; off < 3; off++) {
                        const col = ws.getColumn(c + off);
                        if (col.width === undefined || col.width < 12) col.width = 12;
                    }
                }
                ws.getColumn(curvePointsStartCol).width = 10;
                ws.getColumn(curvePointsStartCol + 1).width = 10;
            };

            startOperation("Building Excel export: fitting master curve + each RCC...");

            // ---------- Sheet 1: Master Curve ----------
            // Uses the current header-checkbox enabled set (so the export
            // reflects what the user currently has enabled, not necessarily
            // what the last Generate Master Curve captured). No IGCC, no
            // unknowns - this sheet is purely the pooled master fit.
            const masterEnabled = [];
            for (let i = 0; i < visibleRccCount; i++) {
                if (selectedRCCs[i]) masterEnabled.push(i + 1);
            }
            const masterContextLabel = `Master Curve (${masterEnabled.length} RCC${masterEnabled.length === 1 ? "" : "s"}${(visibleRccCount - masterEnabled.length) > 0 ? `, excluded ${visibleRccCount - masterEnabled.length}` : ""})`;

            const wsMaster = wb.addWorksheet("Master Curve");
            writeHeader(wsMaster, "Master Curve", masterContextLabel);

            // Analyzer/Calibrator ID rows on the master sheet show every
            // visible RCC, with excluded ones marked with an X in the title
            // row of the RCC Data block below.
            wsMaster.getRow(6).getCell(1).value = "Reference"; wsMaster.getRow(6).getCell(2).value = "Analyzer ID";
            for (let i = 0; i < visAnalyzerIds.length; i++) {
                wsMaster.getCell(6, 3 + i).value = visAnalyzerIds[i];
            }
            wsMaster.getRow(7).getCell(1).value = "Calibrator"; wsMaster.getRow(7).getCell(2).value = "ID";
            for (let i = 0; i < visCalIds.length; i++) {
                wsMaster.getCell(7, 3 + i).value = visCalIds[i];
            }

            const masterRccTable = writeRccTable(wsMaster, 9, Array.from({ length: visibleRccCount }, (_, i) => i + 1));
            // Mark excluded RCCs in the master table title row.
            for (let i = 0; i < visibleRccCount; i++) {
                if (!selectedRCCs[i]) {
                    const c = wsMaster.getCell(9, 3 + i);
                    c.value = "X"; c.font = { bold: true, color: { argb: "FFC00000" } }; c.alignment = { horizontal: "center" };
                }
            }

            const masterFit = masterEnabled.length > 0
                ? await runFit({ rccSet: masterEnabled })
                : { ok: false, reason: "no RCCs enabled" };

            let masterChartRow = null;
            if (masterFit.ok) {
                const sec = writeFitSection(wsMaster, masterRccTable.nextRow + 2, masterFit, null, null, null);
                masterChartRow = sec.mgccRowStart;
            } else {
                wsMaster.getCell(masterRccTable.nextRow + 2, 1).value = `Master fit unavailable: ${masterFit.reason}`;
                wsMaster.getCell(masterRccTable.nextRow + 2, 1).font = { bold: true, color: { argb: "FFC00000" } };
            }
            applyColumnWidths(wsMaster, masterRccTable.blockCols, 21);

            // ---------- Sheets 2..N+1: one per visible RCC ----------
            // Each RCC sheet shows that column's own 1-RCC curve, the IGCC
            // pairs the user typed for that tab, the unknown ALU/Dose tables
            // for that tab, and a fresh fit + curve points (re-fetched so
            // numbers are authoritative even if the user never clicked
            // Generate on that tab).
            const perRccSummaries = [];
            for (let rccIdx = 0; rccIdx < visibleRccCount; rccIdx++) {
                const rccNum = rccIdx + 1;
                // Surface progress while the loop runs - 24 sequential
                // backend calls is long enough that an unchanged status
                // banner can look frozen.
                startOperation(`Building Excel export: fitting RCC-${rccNum} (${rccNum} of ${visibleRccCount})...`);
                const sheetName = `RCC-${rccNum}`;
                const ws = wb.addWorksheet(sheetName);

                const tabIgccRows = igccByRcc[rccNum] || makeDefaultIgccRows();
                const tabUnkAluRows = unknownAluByRcc[rccNum] || makeDefaultUnknownAluRows();
                const tabUnkDoseRows = unknownDoseByRcc[rccNum] || makeDefaultUnknownDoseRows();

                // Collect inputs that influence the fit / xconc / ysignal
                // for this RCC's sheet.
                const igccPairs = tabIgccRows
                    .map(r => ({ alu: parseMaybeNumber(r.inputAlu), dose: parseMaybeNumber(r.inputDose) }))
                    .filter(p => p.alu !== null && p.dose !== null);
                const unknownAlus = tabUnkAluRows.map(r => parseMaybeNumber(r.inputAlu));
                const unknownDoses = tabUnkDoseRows.map(r => parseMaybeNumber(r.inputDose));

                writeHeader(ws, sheetName, `RCC-${rccNum} only${selectedRCCs[rccIdx] ? "" : " (excluded from Master)"}`);
                ws.getCell(6, 1).value = "Reference"; ws.getCell(6, 2).value = "Analyzer ID";
                ws.getCell(6, 3).value = visAnalyzerIds[rccIdx] || "";
                ws.getCell(7, 1).value = "Calibrator"; ws.getCell(7, 2).value = "ID";
                ws.getCell(7, 3).value = visCalIds[rccIdx] || "";

                const tableInfo = writeRccTable(ws, 9, [rccNum]);

                // Always run a plain MGCC fit (no IGCC pairs) for the
                // MGCC chart on every RCC sheet. When the tab has IGCC
                // pairs filled in, also run a second fit WITH those pairs
                // so we can embed the bent curve as a second chart and use
                // its authoritative unknown evaluations in the tables.
                const plainFit = await runFit({
                    rccFilter: rccNum,
                    unknownAlus,
                    unknownDoses
                });
                const igccFit = igccPairs.length > 0
                    ? await runFit({
                        rccFilter: rccNum,
                        igccPairs,
                        unknownAlus,
                        unknownDoses
                    })
                    : null;
                // The "authoritative" fit drives the tables / unknowns
                // section.  Prefer the IGCC fit when applied so the export
                // matches what the user sees on-page after Calculate IGCC.
                const rccFit = (igccFit && igccFit.ok) ? igccFit : plainFit;

                let chartRow = null;
                if (rccFit.ok) {
                    // Project the backend's unknown answers back onto the
                    // tab's row tables so the exported sheet matches what
                    // the user would see after clicking Calculate IGCC /
                    // Find Unknowns on that tab.
                    const renderedUnkAlu = tabUnkAluRows.map((r, i) => {
                        if (unknownAlus[i] === null) return { ...r, fittedDose: "" };
                        const v = rccFit.unknownDosesFromAlu[i];
                        return {
                            ...r,
                            fittedDose: (v == null || !Number.isFinite(Number(v))) ? "" : fmt(v)
                        };
                    });
                    const renderedUnkDose = tabUnkDoseRows.map((r, i) => {
                        if (unknownDoses[i] === null) return { ...r, fittedAlu: "" };
                        const v = rccFit.unknownAlusFromDose[i];
                        return {
                            ...r,
                            fittedAlu: (v == null || !Number.isFinite(Number(v))) ? "" : fmt(v)
                        };
                    });

                    const sec = writeFitSection(
                        ws, tableInfo.nextRow + 2, rccFit,
                        tabIgccRows, renderedUnkAlu, renderedUnkDose
                    );
                    chartRow = sec.mgccRowStart;

                    // Embed the MGCC chart (plain 1-RCC fit) for this RCC,
                    // mirroring the on-page chart layout. Pulled from
                    // plainFit so the curve shown here is the unbent fit
                    // even when igccFit overrides the tables above.
                    try {
                        if (plainFit.ok) {
                            const rawPts = plainFit.concs.map((c, i) => ({ x: c, y: plainFit.pooledSignals[i] }));
                            const svgStr = buildChartSvgString({
                                linePts: plainFit.curvePoints,
                                rawPts,
                                igccPts: [],
                                title: chartTitle,
                                subtitle: `RCC-${rccNum} MGCC fit`
                            });
                            const blob = await rasterizeSvgToPng(svgStr);
                            const buf = await blobToArrayBuffer(blob);
                            const imgId = wb.addImage({ buffer: buf, extension: "png" });
                            ws.addImage(imgId, {
                                tl: { col: 7, row: chartRow - 1 },
                                br: { col: 19, row: chartRow + 30 }
                            });
                        }
                    } catch (err) {
                        console.warn(`RCC-${rccNum} MGCC chart embed failed:`, err);
                    }

                    // When IGCC pairs were applied, embed a second chart
                    // immediately below showing the bent curve + pseudocal
                    // markers. The on-page chart only ever shows one curve
                    // at a time; this is the export's only place where the
                    // reviewer can see both the unbent and bent shapes side
                    // by side for this RCC.
                    if (igccFit && igccFit.ok && igccFit.igccApplied) {
                        try {
                            const rawPts = igccFit.concs.map((c, i) => ({ x: c, y: igccFit.pooledSignals[i] }));
                            const igccPts = igccPairs.map(p => ({ x: p.dose, y: p.alu }));
                            const svgStr = buildChartSvgString({
                                linePts: igccFit.curvePoints,
                                rawPts,
                                igccPts,
                                title: chartTitle,
                                subtitle: `RCC-${rccNum} IGCC bent (${igccPairs.length} re-calibrator${igccPairs.length === 1 ? "" : "s"})`
                            });
                            const blob = await rasterizeSvgToPng(svgStr);
                            const buf = await blobToArrayBuffer(blob);
                            const imgId = wb.addImage({ buffer: buf, extension: "png" });
                            // Anchor 32 rows below the MGCC chart so the
                            // two charts don't overlap. tl/br use the same
                            // column range as the MGCC chart above.
                            ws.addImage(imgId, {
                                tl: { col: 7, row: chartRow + 31 },
                                br: { col: 19, row: chartRow + 62 }
                            });
                        } catch (err) {
                            console.warn(`RCC-${rccNum} IGCC chart embed failed:`, err);
                        }
                    }

                    perRccSummaries.push({
                        rcc: rccNum, ok: true,
                        igccApplied: !!(igccFit && igccFit.ok && igccFit.igccApplied),
                        igccCount: igccPairs.length,
                        unkAluCount: unknownAlus.filter(v => v !== null).length,
                        unkDoseCount: unknownDoses.filter(v => v !== null).length
                    });
                } else {
                    ws.getCell(tableInfo.nextRow + 2, 1).value = `RCC-${rccNum} fit unavailable: ${rccFit.reason}`;
                    ws.getCell(tableInfo.nextRow + 2, 1).font = { bold: true, color: { argb: "FFC00000" } };
                    perRccSummaries.push({ rcc: rccNum, ok: false, reason: rccFit.reason });
                }
                applyColumnWidths(ws, tableInfo.blockCols, 21);
            }

            // ---------- Embed the Master Curve chart ----------
            // Generated from the master fit data (not from the live on-page
            // SVG), so the master sheet always shows the master curve even
            // when the user is currently viewing a per-RCC fit on the page.
            let pngEmbedded = false;
            if (masterFit.ok && masterChartRow != null) {
                try {
                    const rawPts = masterFit.concs.map((c, i) => ({ x: c, y: masterFit.pooledSignals[i] }));
                    const svgStr = buildChartSvgString({
                        linePts: masterFit.curvePoints,
                        rawPts,
                        igccPts: [],
                        title: chartTitle,
                        subtitle: masterContextLabel
                    });
                    const blob = await rasterizeSvgToPng(svgStr);
                    const buf = await blobToArrayBuffer(blob);
                    const imgId = wb.addImage({ buffer: buf, extension: "png" });
                    wsMaster.addImage(imgId, {
                        tl: { col: 7, row: masterChartRow - 1 },
                        br: { col: 19, row: masterChartRow + 30 }
                    });
                    pngEmbedded = true;
                } catch (err) {
                    console.warn("Master chart embed failed:", err);
                }
            }

            // ---------- Save the workbook ----------
            const xlsxBuffer = await wb.xlsx.writeBuffer();
            saveAs(
                new Blob([xlsxBuffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                }),
                `G06_Export_${stampForFile}.xlsx`
            );

            const failedFits = perRccSummaries.filter(s => !s.ok).length;
            const bentCount = perRccSummaries.filter(s => s.ok && s.igccApplied).length;
            const tail = [
                masterFit.ok ? null : `master fit skipped (${masterFit.reason})`,
                failedFits > 0 ? `${failedFits} RCC sheet${failedFits === 1 ? "" : "s"} skipped` : null,
                pngEmbedded ? null : "master chart embed skipped"
            ].filter(Boolean).join("; ");
            const tailMsg = tail ? ` (${tail})` : "";
            finishOperation(
                `Workbook exported: 1 master + ${visibleRccCount} RCC sheets, ${bentCount} with IGCC bend, charts embedded on each sheet.${tailMsg}`,
                "success"
            );
        } catch (err) {
            console.error(err);
            showError("Export Failed", err?.message || "Failed to build Excel workbook.");
            finishOperation("Export to Excel failed.", "error");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>G06: Generate Master Curve</Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                Pool every enabled RCC column into one protein-5 master curve. For per-assay
                IGCC re-calibration and unknown back-prediction, use the <b>G06 Multi-IGCC/RCC</b> page.
            </Typography>

            <Button
                onClick={() => setMetaOpen(!metaOpen)}
                startIcon={metaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 1 }}
            >
                Assay Metadata
            </Button>

            <Collapse in={metaOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>Assay Identification</Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Analyte Code"
                            size="small"
                            value={metadata.analyteCode}
                            onChange={(e) => setMetadata({ ...metadata, analyteCode: e.target.value })}
                        />
                        <TextField
                            label="Assay Name"
                            size="small"
                            value={metadata.assayName}
                            onChange={(e) => setMetadata({ ...metadata, assayName: e.target.value })}
                        />
                        <TextField
                            label="Reagent Lot"
                            size="small"
                            value={metadata.reagentLot}
                            onChange={(e) => setMetadata({ ...metadata, reagentLot: e.target.value })}
                        />
                        <TextField
                            label="MGCC Id"
                            size="small"
                            value={metadata.mgccId}
                            onChange={(e) => setMetadata({ ...metadata, mgccId: e.target.value })}
                        />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField
                            label="Protocol Id"
                            size="small"
                            value={metadata.protocolId}
                            onChange={(e) => setMetadata({ ...metadata, protocolId: e.target.value })}
                        />
                        <TextField
                            label="Protocol #"
                            size="small"
                            value={metadata.protocolNumber}
                            onChange={(e) => setMetadata({ ...metadata, protocolNumber: e.target.value })}
                        />
                        <TextField
                            label="Owner"
                            size="small"
                            value={metadata.owner}
                            onChange={( e) => setMetadata({ ...metadata, owner: e.target.value })}
                        />
                        <TextField
                            label="Date"
                            size="small"
                            value={metadata.date}
                            onChange={(e) => setMetadata({ ...metadata, date: e.target.value })}
                        />
                        <TextField
                            label="Gem ID"
                            size="small"
                            value={metadata.genId}
                            onChange={(e) => setMetadata({ ...metadata, genId: e.target.value })}
                        />
                    </Box>
                </Paper>
            </Collapse>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Button
                    onClick={() => setRccOpen(!rccOpen)}
                    startIcon={rccOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ mb: 0 }}
                >
                    RCC Data
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddColumns(1)}
                    disabled={visibleRccCount >= RCC_COUNT}
                    sx={{ minWidth: 32, width: 32, height: 32, p: 0, fontSize: 18, lineHeight: 1 }}
                    title="Add 1 RCC column"
                >
                    +
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleRemoveColumns(1)}
                    disabled={visibleRccCount <= MIN_RCC_COLS}
                    sx={{ minWidth: 32, width: 32, height: 32, p: 0, fontSize: 18, lineHeight: 1 }}
                    title="Remove 1 RCC column"
                >
                    -
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddColumns(10)}
                    disabled={visibleRccCount >= RCC_COUNT}
                    sx={{ minWidth: 46, height: 32, p: 0.5, fontSize: 12, lineHeight: 1 }}
                    title="Add 10 RCC columns"
                >
                    +10
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleRemoveColumns(10)}
                    disabled={visibleRccCount <= MIN_RCC_COLS}
                    sx={{ minWidth: 46, height: 32, p: 0.5, fontSize: 12, lineHeight: 1 }}
                    title="Remove 10 RCC columns"
                >
                    -10
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleClearRccData}
                    sx={{ height: 32 }}
                    title="Clear all concentration / signal cells and reset analyzer IDs, calibrator IDs, row count, and column count to defaults. Does not affect IGCC, unknowns, or fit results."
                >
                    Clear RCC Data
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ height: 32 }}
                >
                    Import RCC Data
                </Button>
                <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    ref={fileInputRef}
                    onChange={handleImportMGCC}
                    style={{ display: "none" }}
                />
            </Box>

            <Collapse in={rccOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box
                        ref={gridContainerRef}
                        sx={{
                            width: "100%",
                            overflowY: "hidden",
                            overflowX: visibleRccCount > DEFAULT_VISIBLE_RCC_COLS ? "auto" : "hidden"
                        }}
                    >
                        <Box sx={{ width: effectiveGridWidth, mb: 1 }}>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: `${REF_COL_WIDTH}px ${CONC_COL_WIDTH}px repeat(${visibleRccCount}, ${rccColWidth}px)`,
                                    alignItems: "center",
                                    gap: 0
                                }}
                            >
                                <Typography sx={{ fontSize: 12, fontWeight: 600, px: 0.5 }}>Reference</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, px: 0.5 }}>Analyzer ID</Typography>
                                {Array.from({ length: visibleRccCount }, (_, i) => (
                                    <TextField
                                        key={`analyzer-${i}`}
                                        size="small"
                                        value={rccAnalyzerIds[i] || ""}
                                        onChange={(e) => {
                                            const copy = [...rccAnalyzerIds];
                                            copy[i] = e.target.value;
                                            setRccAnalyzerIds(copy);
                                        }}
                                        sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: 12 } }}
                                    />
                                ))}

                                <Typography sx={{ fontSize: 12, fontWeight: 600, px: 0.5 }}>Calibrator</Typography>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, px: 0.5 }}>ID</Typography>
                                {Array.from({ length: visibleRccCount }, (_, i) => (
                                    <TextField
                                        key={`id-${i}`}
                                        size="small"
                                        value={rccIds[i] || ""}
                                        onChange={(e) => {
                                            const copy = [...rccIds];
                                            copy[i] = e.target.value;
                                            setRccIds(copy);
                                        }}
                                        sx={{ "& .MuiInputBase-input": { py: 0.5, fontSize: 12 } }}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ width: effectiveGridWidth }}>
                            <G06BlockDataGrid
                                columnDefs={columnDefs}
                                data={gridData.slice(0, rccRowCount).map((row, idx) => {
                                    const rowData = { id: idx + 1, Ref: idx + 1, Concentration: row.concentration };
                                    for (let r = 1; r <= RCC_COUNT; r++) {
                                        rowData[`RCC-${r}`] = row[`RCC-${r}`];
                                    }
                                    return rowData;
                                })}
                                onDataChange={(rows) => {
                                    const copy = [...gridData];
                                    rows.slice(0, rccRowCount).forEach((row, i) => {
                                        if (!copy[i]) copy[i] = createEmptyRccRow(i);
                                        copy[i].concentration = parseMaybeNumber(row["Concentration"]);
                                        for (let r = 1; r <= RCC_COUNT; r++) {
                                            copy[i][`RCC-${r}`] = parseMaybeNumber(row[`RCC-${r}`]);
                                        }
                                    });
                                    setGridData(copy);
                                }}
                                rowCount={rccRowCount}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Collapse>

            <Divider sx={{ my: 2 }} />

            <Button
                onClick={() => setRoundOpen(!roundOpen)}
                startIcon={roundOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 1 }}
            >
                Rounding
            </Button>

            <Collapse in={roundOpen}>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                            label="Sig Digits"
                            type="number"
                            size="small"
                            value={rounding.significantDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    significantDigits: Number(e.target.value)
                                })
                            }
                        />
                        <TextField
                            label="Precision"
                            type="number"
                            size="small"
                            value={rounding.precisionDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    precisionDigits: Number(e.target.value)
                                })
                            }
                        />
                        <TextField
                            label="CV Sig Digits"
                            type="number"
                            size="small"
                            value={rounding.cvSignificantDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    cvSignificantDigits: Number(e.target.value)
                                })
                            }
                        />
                        <TextField
                            label="CV Precision"
                            type="number"
                            size="small"
                            value={rounding.cvPrecisionDigits}
                            onChange={(e) =>
                                setRounding({
                                    ...rounding,
                                    cvPrecisionDigits: Number(e.target.value)
                                })
                            }
                        />
                    </Box>
                </Paper>
            </Collapse>

            <Divider sx={{ my: 2 }} />

            {/* Per-assay IGCC re-calibration, unknown back-prediction, and the
                per-RCC tab strip have moved to the G06 Multi-IGCC/RCC page.
                This page is the master-curve generator only. */}
            <Divider sx={{ my: 2 }} />

            <Alert
                severity={
                    operationStatus.level === "error" ? "error"
                    : operationStatus.level === "warning" ? "warning"
                    : operationStatus.level === "success" ? "success"
                    : "info"
                }
                icon={
                    operationStatus.busy
                        ? <CircularProgress size={16} />
                        : operationStatus.level === "error"
                            ? <ErrorOutlineIcon fontSize="small" />
                            : operationStatus.level === "warning"
                                ? <WarningAmberIcon fontSize="small" />
                                : operationStatus.level === "success"
                                    ? <CheckCircleOutlineIcon fontSize="small" />
                                    : undefined
                }
                sx={{ mb: 2 }}
            >
                {operationStatus.busy ? "Working... " : ""}{operationStatus.message}
                {operationStatus.at && (
                    <Box
                        component="span"
                        sx={{ ml: 1.5, opacity: 0.7, fontFamily: "monospace", fontSize: "0.85em" }}
                    >
                        [{operationStatus.at.toLocaleString()}]
                    </Box>
                )}
            </Alert>

            <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mb: 1, alignItems: "center" }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleGenerate("master")}
                    disabled={operationStatus.busy}
                    title="Fit the protein-5 curve across every enabled RCC column. Excluded RCCs (header checkbox unticked) are dropped."
                >
                    Generate Master Curve
                </Button>
                <Box sx={{ flex: 1 }} />
                {/* Chart title customizer - lives next to the action buttons
                    so the user can tweak it right before clicking Generate /
                    Export. Empty input falls back to the default label so the
                    chart never renders a blank title bar. */}
                <TextField
                    label="Chart Title"
                    size="small"
                    value={chartTitle}
                    onChange={(e) => setChartTitle(e.target.value || "Dose Curve Response")}
                    sx={{ minWidth: 260 }}
                    title="Title rendered at the top of the on-page dose-response chart and every chart embedded in the Excel export."
                />
            </Box>

            <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, mb: 1, color: "text.secondary", fontSize: 11 }}
            >
                Last master fit: {lastMasterPool
                    ? `Master Curve across ${visibleRccCount - (lastMasterPool.excluded || []).length} RCC${(visibleRccCount - (lastMasterPool.excluded || []).length) === 1 ? "" : "s"}${(lastMasterPool.excluded || []).length > 0 ? ` (excluded RCC-${(lastMasterPool.excluded || []).join(", RCC-")})` : ""}.`
                    : "none - run Generate Master Curve to fit the pool."}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Collapse in={resultOpen}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Typography variant="h6">MGCC Information</Typography>
                        <Button variant="outlined" size="small" onClick={exportToExcel}>
                            Export to Excel
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            gap: 6,
                            mt: 2,
                            alignItems: "flex-start"
                        }}
                    >
                        <Box sx={{ minWidth: 620, flex: "0 0 620px" }}>
                            <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                                <TextField label="Fit Factor" fullWidth value={fitFactor} />
                                <TextField label="Shape Factor" fullWidth value={shapeFactor} />
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(5, 1fr)",
                                    gap: 1,
                                    mb: 2
                                }}
                            >
                                <TextField label="a" value={abcdn.a} />
                                <TextField label="b" value={abcdn.b} />
                                <TextField label="c" value={abcdn.c} />
                                <TextField label="d" value={abcdn.d} />
                                <TextField label="n" value={abcdn.n} />
                            </Box>

                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: "0.5fr 1fr 1fr 1fr 1fr 1fr",
                                    gap: 1
                                }}
                            >
                                <Typography fontWeight="bold">Ref</Typography>
                                <Typography fontWeight="bold">Conc</Typography>
                                <Typography fontWeight="bold">Pooled</Typography>
                                <Typography fontWeight="bold">%CV</Typography>
                                <Typography fontWeight="bold">Fit Conc</Typography>
                                <Typography fontWeight="bold">Fit Signal</Typography>

                                {mgccRows.map((r) => (
                                    <React.Fragment key={r.id}>
                                        <Typography sx={{ display: "flex", alignItems: "center" }}>{r.ref}</Typography>
                                        <TextField size="small" value={r.conc ?? ""} />
                                        <TextField size="small" value={r.pooled ?? ""} />
                                        <TextField size="small" value={r.cv ?? ""} />
                                        <TextField size="small" value={r.fittedConc ?? ""} />
                                        <TextField size="small" value={r.fittedSignal ?? ""} />
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                flex: "0 0 874px",
                                width: 874,
                                border: "1px solid #ccc",
                                borderRadius: 1,
                                height: 633,
                                backgroundColor: "#fafafa",
                                p: 1,
                                position: "relative"
                            }}
                        >
                            {activeFitLabel && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: 6,
                                        left: 10,
                                        zIndex: 3,
                                        maxWidth: 300,
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                        color: "#1976d2",
                                        border: "1px solid rgba(25, 118, 210, 0.3)",
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.25,
                                        fontSize: 11,
                                        fontWeight: 600,
                                        letterSpacing: 0.2,
                                        lineHeight: 1.25,
                                        pointerEvents: "none"
                                    }}
                                >
                                    Currently viewing: {activeFitLabel} fit
                                </Box>
                            )}
                            <svg
                                ref={svgRef}
                                width="100%"
                                height="100%"
                                viewBox="0 0 874 633"
                                preserveAspectRatio="none"
                            >
                                {(() => {
                                    const all = [...graphLinePoints, ...graphRawPoints, ...graphIgccPoints];
                                    if (all.length === 0) return null;

                                    const xs = all.map((p) => p.x);
                                    const ys = all.map((p) => p.y);

                                    const minX = Math.min(...xs);
                                    const maxX = Math.max(...xs);
                                    const minY = Math.min(...ys);
                                    const maxY = Math.max(...ys);

                                    const leftPad = 70;
                                    const rightPad = 20;
                                    const topPad = 65;
                                    const bottomPad = 50;
                                    const chartWidth = 874 - leftPad - rightPad;
                                    const chartHeight = 633 - topPad - bottomPad;

                                    const sx = (x) =>
                                        leftPad +
                                        ((x - minX) / ((maxX - minX) || 1)) * chartWidth;

                                    const sy = (y) =>
                                        topPad +
                                        chartHeight -
                                        ((y - minY) / ((maxY - minY) || 1)) * chartHeight;

                                    const linePoints = graphLinePoints
                                        .map((p) => `${sx(p.x)},${sy(p.y)}`)
                                        .join(" ");

                                    const xTicks = Array.from({ length: 5 }, (_, i) => {
                                        const value = minX + ((maxX - minX) * i) / 4;
                                        return { value, x: sx(value) };
                                    });

                                    const yTicks = Array.from({ length: 5 }, (_, i) => {
                                        const value = minY + ((maxY - minY) * (4 - i)) / 4;
                                        return { value, y: sy(value) };
                                    });

                                    return (
                                        <>
                                            <text x={leftPad + chartWidth / 2} y="50" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">
                                                {chartTitle}
                                            </text>

                                            <line x1={leftPad} y1={topPad + chartHeight} x2={leftPad + chartWidth} y2={topPad + chartHeight} stroke="#555" />
                                            <line x1={leftPad} y1={topPad} x2={leftPad} y2={topPad + chartHeight} stroke="#555" />

                                            {xTicks.map((tick, i) => (
                                                <g key={`xtick-${i}`}>
                                                    <line x1={tick.x} y1={topPad + chartHeight} x2={tick.x} y2={topPad + chartHeight + 5} stroke="#555" />
                                                    <text x={tick.x} y={topPad + chartHeight + 17} textAnchor="middle" fontSize="10" fill="#333">
                                                        {Number(tick.value).toFixed(2)}
                                                    </text>
                                                </g>
                                            ))}

                                            {yTicks.map((tick, i) => (
                                                <g key={`ytick-${i}`}>
                                                    <line x1={leftPad - 5} y1={tick.y} x2={leftPad} y2={tick.y} stroke="#555" />
                                                    <line x1={leftPad} y1={tick.y} x2={leftPad + chartWidth} y2={tick.y} stroke="#e0e0e0" />
                                                    <text x={leftPad - 8} y={tick.y + 3} textAnchor="end" fontSize="10" fill="#333">
                                                        {Number(tick.value).toFixed(2)}
                                                    </text>
                                                </g>
                                            ))}

                                            <text
                                                x={leftPad + chartWidth / 2}
                                                y={topPad + chartHeight + 40}
                                                textAnchor="middle"
                                                fontSize="12"
                                                fontWeight="bold"
                                                fill="#333"
                                            >
                                                Concentration
                                            </text>

                                            <text
                                                x="12"
                                                y={topPad + chartHeight / 2}
                                                textAnchor="middle"
                                                fontSize="12"
                                                fontWeight="bold"
                                                fill="#333"
                                                transform={`rotate(-90 12 ${topPad + chartHeight / 2})`}
                                            >
                                                Signal
                                            </text>

                                            <polyline fill="none" stroke="#1976d2" strokeWidth="2" points={linePoints} />

                                            {graphLinePoints.map((p, i) => (
                                                <circle
                                                    key={`curve-${i}`}
                                                    cx={sx(p.x)}
                                                    cy={sy(p.y)}
                                                    r={3}
                                                    fill="#1976d2"
                                                    stroke="white"
                                                    strokeWidth={1.5}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={() => setHoveredPoint({
                                                        type: "Fitted Curve", 
                                                        x: p.x, 
                                                        y: p.y,
                                                        svgX: sx(p.x),
                                                        svgY: sy(p.y)
                                                    })}
                                                    onMouseLeave={() => setHoveredPoint(null)}
                                                />
                                            ))}

                                            {graphRawPoints.map((p, i) => (
                                                <circle
                                                    key={`raw-${i}`}
                                                    cx={sx(p.x)}
                                                    cy={sy(p.y)}
                                                    r={4}
                                                    fill="#d32f2f"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={() => setHoveredPoint({
                                                        type: "Raw Data", 
                                                        x: p.x, 
                                                        y: p.y,
                                                        svgX: sx(p.x),
                                                        svgY: sy(p.y)
                                                    })}
                                                    onMouseLeave={() => setHoveredPoint(null)}
                                                />
                                            ))}

                                            {graphIgccPoints.map((p, i) => {
                                                const cx = sx(p.x);
                                                const cy = sy(p.y);
                                                const arm = 6;
                                                return (
                                                    <g
                                                        key={`igcc-${i}`}
                                                        style={{ cursor: 'pointer' }}
                                                        onMouseEnter={() => setHoveredPoint({
                                                            type: `Pseudocal ${p.label || ""}`.trim(),
                                                            x: p.x,
                                                            y: p.y,
                                                            svgX: cx,
                                                            svgY: cy
                                                        })}
                                                        onMouseLeave={() => setHoveredPoint(null)}
                                                    >
                                                        {/* Transparent hit target so the thin + is easy to hover */}
                                                        <rect
                                                            x={cx - arm - 2}
                                                            y={cy - arm - 2}
                                                            width={(arm + 2) * 2}
                                                            height={(arm + 2) * 2}
                                                            fill="transparent"
                                                        />
                                                        <line
                                                            x1={cx - arm} y1={cy}
                                                            x2={cx + arm} y2={cy}
                                                            stroke="#7b1fa2"
                                                            strokeWidth={2.25}
                                                            strokeLinecap="round"
                                                        />
                                                        <line
                                                            x1={cx} y1={cy - arm}
                                                            x2={cx} y2={cy + arm}
                                                            stroke="#7b1fa2"
                                                            strokeWidth={2.25}
                                                            strokeLinecap="round"
                                                        />
                                                    </g>
                                                );
                                            })}

                                            {/* Inline legend (top-right of the chart area) */}
                                            {(() => {
                                                const legendX = leftPad + chartWidth - 130;
                                                const legendY = topPad + 6;
                                                const rowH = 16;
                                                return (
                                                    <g>
                                                        <rect
                                                            x={legendX - 8}
                                                            y={legendY - 4}
                                                            width={130}
                                                            height={rowH * 3 + 8}
                                                            fill="#ffffff"
                                                            fillOpacity={0.85}
                                                            stroke="#bbb"
                                                            strokeWidth={0.5}
                                                            rx={3}
                                                        />
                                                        {/* Fitted Curve */}
                                                        <line
                                                            x1={legendX} y1={legendY + 6}
                                                            x2={legendX + 18} y2={legendY + 6}
                                                            stroke="#1976d2" strokeWidth={2}
                                                        />
                                                        <circle cx={legendX + 9} cy={legendY + 6} r={3} fill="#1976d2" />
                                                        <text x={legendX + 24} y={legendY + 9} fontSize="11" fill="#333">
                                                            Fitted Curve
                                                        </text>
                                                        {/* Raw Data */}
                                                        <circle cx={legendX + 9} cy={legendY + rowH + 6} r={4} fill="#d32f2f" />
                                                        <text x={legendX + 24} y={legendY + rowH + 9} fontSize="11" fill="#333">
                                                            Raw Data
                                                        </text>
                                                        {/* Pseudocal (IGCC) */}
                                                        <line
                                                            x1={legendX + 3} y1={legendY + rowH * 2 + 6}
                                                            x2={legendX + 15} y2={legendY + rowH * 2 + 6}
                                                            stroke="#7b1fa2" strokeWidth={2.25} strokeLinecap="round"
                                                        />
                                                        <line
                                                            x1={legendX + 9} y1={legendY + rowH * 2}
                                                            x2={legendX + 9} y2={legendY + rowH * 2 + 12}
                                                            stroke="#7b1fa2" strokeWidth={2.25} strokeLinecap="round"
                                                        />
                                                        <text x={legendX + 24} y={legendY + rowH * 2 + 9} fontSize="11" fill="#333">
                                                            Pseudocal
                                                        </text>
                                                    </g>
                                                );
                                            })()}

                                            {hoveredPoint && (
                                                <>
                                                    <line
                                                        x1={sx(hoveredPoint.x)}
                                                        y1={topPad}
                                                        x2={sx(hoveredPoint.x)}
                                                        y2={topPad + chartHeight}
                                                        stroke="#333"
                                                        strokeWidth={1}
                                                        strokeDasharray="4 4"
                                                    />
                                                    <line
                                                        x1={leftPad}
                                                        y1={sy(hoveredPoint.y)}
                                                        x2={leftPad + chartWidth}
                                                        y2={sy(hoveredPoint.y)}
                                                        stroke="#333"
                                                        strokeWidth={1}
                                                        strokeDasharray="4 4"
                                                    />
                                                </>
                                            )}
                                        </>
                                    );
                                })()}
                            </svg>

                            {hoveredPoint && (
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: hoveredPoint.svgX,
                                        top: hoveredPoint.svgY,
                                        transform: "translate(10px, -110%)",
                                        backgroundColor: "#222",
                                        color: "#fff",
                                        px: 1.25,
                                        py: 0.75,
                                        borderRadius: 1,
                                        fontSize: 12,
                                        pointerEvents: "none",
                                        boxShadow: 3,
                                        whiteSpace: "nowrap",
                                        zIndex: 2
                                    }}
                                >
                                    <div>{hoveredPoint.type}</div>
                                    <div>Dose: {Number(hoveredPoint.x).toFixed(4)}</div>
                                    <div>Signal: {Number(hoveredPoint.y).toFixed(4)}</div>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Collapse>

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid #eee",
                    color: "text.secondary",
                    fontSize: 11
                }}
            >
                Frontend v{APP_VERSION} {"\u2022"} Math library {MATH_LIB_VERSION}
            </Typography>
        </Box>
    );
};

export default G06;
